import cloneDeep from 'lodash.clonedeep';
import { hasRequiredUPQFields } from '@makemydeal/dr-payment-utils';
import type { UPQRequiredFields } from '@makemydeal/dr-payment-utils';
// eslint-disable-next-line @typescript-eslint/no-var-requires
const has = require('lodash.has');
// eslint-disable-next-line @typescript-eslint/no-var-requires
const sortBy = require('lodash.sortby');
import type { AnyFSA } from '@makemydeal/dr-browser-utils';
import { roundToTwoDecimal } from '@makemydeal/dr-common-utils';
import type {
    AccelerateVehicleCondition,
    AcceleronTaxItems,
    FeeOverride,
    GenericObject,
    IIncentives,
    IPaymentOverride,
    IPaymentOverrideCash,
    IPaymentOverrideFinance,
    IPaymentOverrideLease,
    IPaymentServicesOverridesCash,
    IPaymentTradeIn,
    ITradeIn,
    IVehicleForPayment,
    ManualIncentiveEntry,
    ManualIncentivesRequest,
    MongoDealer,
    OutsideLienHolder,
    PaymentOverride,
    PaymentSchedule,
    paymentServicesTypes,
    PaymentWithDealerIdRequest,
    PostStageTransformationByType,
    ResidualInfo,
    ResidualValueType,
    SlimTermsIncentives,
    Vehicle,
    OfferType,
    ICashResponse
} from '@makemydeal/dr-platform-types';
import {
    ApplicationCodes,
    CASH,
    FINANCE,
    LEASE,
    BALLOON,
    OFFER_ENCRYPTION_FIELDS,
    VEHICLE_ENCRYPTION_FIELDS,
    RequestType,
    ExtendedRequestType,
    RESIDUAL_DOLLAR,
    RESIDUAL_PERCENT,
    SpecialPaymentFormula,
    PaymentScheduleTypes
} from '@makemydeal/dr-platform-types';

import { v4 as uuid } from 'uuid';
import { getDefaultTerms } from '.';
import { ACQUISITION_FEE_ID, DEFAULT_RATE_TYPE_MAPPING, PAYMENT_REQUEST_OVERRIDE_NODE } from '../constants/payment';
import { EXPLICIT_LENDER_P1_TOGGLE, SHOPPER_LOCATION_CHANGE_META } from '../constants/meta';
import { EXPLICIT_PROGRAM_LOCK_META } from './explicitProgramLockUtils';
import * as offerSelectors from '../selectors';
import { getSelectedTermMonths } from '../selectors/';
import {
    getActionForPayment,
    getIncludeGovernmentFees,
    getIsShopperUpdateFailed,
    getLastPaymentMissingUPQFields,
    getPaymentLocationId,
    getProgramEndDays,
    getTraceId,
    isLeaseResidualMissing,
    shouldIncludeTaxes
} from '../selectors/paymentSelectors';
import { getProfitabilityForPayloadByDealType, hasProfitabilityData } from '../selectors/profitabilitySelectors';
import * as programSelectors from '../selectors/programSelectors';
import {
    isCashManualRebateProvenanceEnabled,
    isCashSwitchStandardRebateEnabled,
    isGridIncentiveManagementEnabled,
    isKeepLenderOnZipChangeEnabled,
    isManualIncentiveByProvenance,
    isProgramIncentivesRestorationEnabled,
    isRequestChosenStandardsEnabled,
    isRollToApplyIncentiveCatalogEnabled,
    isSendVehicleFieldsOnPaymentEnabled,
    isUnpricedTermStandardsCarryEnabled,
    isManualRebateForRequest
} from './incentives';
import { isPricedWithLeaseResidualOverride } from './leaseResidualOverrideUtils';
import type { LeaseResidualOverride } from './leaseResidualOverrideUtils';
import { isSameLenderProgram, isValidProgram } from './lenderProgramUtils';
import { exceedsMoneyFactorBound, isImplausibleMoneyFactor, isRateInAnotherUnit, resolveTermRateType } from './rateUtils';
import {
    APPLIED_PROGRAM,
    APPLY_RECOMMENDED_VEHICLE_PAYMENT_DETAILS,
    APPLY_ROLL_TO_OPTIONS,
    FETCH_QUESTIONS_OR_PAYMENT,
    LOAD_PAYMENT_GRID_LENDER_PROGRAMS,
    LOAD_PAYMENT_GRID_TERM_LENDER_PROGRAM,
    SELECTED_CREDIT_TIER,
    SELECTED_TERM,
    SWITCH_VEHICLE,
    UPDATED_OFFER_TYPE,
    UPDATED_PAYMENT_FREQUENCY,
    UPDATED_SHOPPER_CONTACT,
    UPDATED_SHOPPER_ZIP,
    UPDATE_SELECTED_INCENTIVES
} from '../types/actionTypes';
import type {
    AutoTermIdParams,
    FeatureTogglesAndUrlOnly,
    GetAutoTermId,
    PaymentDealerAddress,
    PaymentPayloadCalculationSettings,
    PriorLeaseBalanceForPayment
} from '../types/commonTypes';
import type {
    CashTransformed,
    FeesOverrideParams,
    FormattedQuestion,
    HostSelectors,
    IApsVehicleNode,
    IPaymentObjectPayload,
    ITerm,
    PaymentReduxAction,
    ProgramFeesOverrideParams,
    Question,
    RequestOverrideOptions
} from '../types/paymentTypes';
import {
    isConditionalRebateKeepGridOpenEnabled,
    transformAppliedIcentivesForPayment,
    transformIncentives,
    transformIncentivesForPayment
} from './incentives';
import {
    getDownPaymentForOfferType,
    getTermsListByStrategy,
    hasUserTermOverrides,
    isApplyingCreditDecision,
    parseSelectedTermMonths,
    shouldUseGenericCalcsStructure
} from './paymentUtils';
import { parseTermId } from './termIdParser';
import { stripCashFromEquityAllocationMethod } from './equityAllocationUtils';
import { isRegularGridRowAction } from './paymentGridUtils';

export const formatBalloonType = (balloonType?: string): string | undefined => {
    if (!balloonType) return balloonType;
    return balloonType.charAt(0).toUpperCase() + balloonType.slice(1);
};

export function getPreferredPaymentTermMonths(payments: ITerm[] = []) {
    const preferredPayment = payments?.find((payment) => payment.isPreferred);
    return preferredPayment?.term?.months;
}

export function getLowestPaymentTermMonths(payments: ITerm[] = []) {
    const orderedPayments = sortBy(payments, ['monthlyPaymentRounded']);
    return orderedPayments[0]?.term.months;
}

export const shouldUseResolutionLogicForDownPaymentForSP = (
    offerType: OfferType,
    options: {
        sourceApplication?: string;
        isDueAtSigninAmountDeduct?: boolean;
    }
): boolean => {
    const isShoppingPlatformApplication = options.sourceApplication === ApplicationCodes.SP;
    const isLeaseOffer = offerType === LEASE;
    const isDueAtSigninExplicitlyDisabled = options.isDueAtSigninAmountDeduct === false;

    // Special case: Shopping Platform + Lease + Due at signing explicitly disabled
    // In this specific scenario, we don't use resolution logic
    if (isShoppingPlatformApplication && isLeaseOffer && isDueAtSigninExplicitlyDisabled) {
        return false;
    }

    // For all other cases, use resolution logic
    return true;
};

/**
 * Whether a request narrowed past the deal's selected term can price itself.
 *
 * `hasRequiredFieldsForUPQ` reads the deal's SELECTED term and the deal level, which is right for every payload
 * that includes the selected term. A residual or sell-rate edit on a non-active grid column narrows `termList`
 * to that column alone, and unticking Auto Select writes no deal-level rate or residual - so the guard saw
 * "missing UPQ fields", downgraded the call to program-quotes, and program-quotes cannot price that column
 * (DE762937).
 *
 * The column carries its own complete pair in `overridesByTerm`, which is what the payload will send, so the
 * request is priceable and must not be downgraded.
 *
 * Narrow on three axes so no other payload's downgrade behaviour moves: redesign LEASE only, and only for a
 * term list that excludes the selected term. Deliberately NOT a general relaxation of the guard - failing it is
 * what triggers `handleUPQProgramFallback`, which re-seeds `baseResidualTypeOverride` to dollar from the
 * lender's term data and would discard a percent residual the user typed (see the note in the lease branch).
 */
/**
 * Whether this column holds the complete pair the payload would send for it.
 *
 * The residual is resolved through the same rule the payload uses, so "priceable" cannot mean a residual the
 * payload will then withhold: a column the build never quoted has only a stale lender figure, and neither side may
 * claim it (DE762937).
 */
const canColumnPriceItself = (state: any, offerType: OfferType, months: number): boolean => {
    const hasOwnRate = offerSelectors.getSellRateOverrideByOfferTypeAndTerm(state, offerType, months, true, true) != null;
    const ownResidual = offerSelectors.getResidualInfoOverrideByOfferTypeTerm(state, offerType, months);
    const residual =
        resolveCompleteResidualUnit(ownResidual) != null
            ? ownResidual
            : getClaimableLenderResidualForTerm(state, offerType, months);

    return hasOwnRate && resolveCompleteResidualUnit(residual) != null;
};

const hasPriceableNarrowedColumn = (state: any, offerType: OfferType, termList?: number[]): boolean => {
    if (offerType !== LEASE || !state.featureToggles?.F298789_ASP_PG_redesign) return false;
    if (!termList?.length) return false;

    const selectedTermMonths = offerSelectors.getSelectedTermMonths(state, offerType);
    if (termList.includes(selectedTermMonths as number)) return false;

    // Every requested column has to be able to price itself; one bare column still fails the quote.
    return termList.every((months) => canColumnPriceItself(state, offerType, months));
};

/**
 * Whether every requested column is one the USER has priced, and can therefore price itself.
 *
 * The rule above keys on the request excluding the selected term, which is the shape a non-active column's edit
 * takes. A residual typed into the deal's OWN column names that term, so it fell through to the deal-level check -
 * and with Auto Select on the deal holds no rate or residual of its own, so the call was downgraded to
 * program-quotes, the one engine that cannot honour what the user typed (DE762937).
 *
 * `hasUserPricedGridColumn` is the same marker the middleware writes when it re-points a column, so the guard and
 * the routing agree by construction: a column routed to user-program-quotes is one this returns true for.
 */
const hasPriceableUserPricedColumns = (state: any, offerType: OfferType, termList?: number[]): boolean => {
    if (offerType !== LEASE || !state.featureToggles?.F298789_ASP_PG_redesign) return false;
    if (!termList?.length) return false;

    return termList.every(
        (months) =>
            offerSelectors.hasUserPricedGridColumn(state, offerType, months) && canColumnPriceItself(state, offerType, months)
    );
};

export const hasRequiredFieldsForUPQ = (state: any, offerType: OfferType, termList?: number[]): boolean => {
    if (hasPriceableNarrowedColumn(state, offerType, termList)) return true;
    if (hasPriceableUserPricedColumns(state, offerType, termList)) return true;

    const selectedTermMonths = offerSelectors.getSelectedTermMonths(state, offerType);
    // The deal's SELECTED term and the deal level, which is what every payload including that term is priced
    // from. No longer the same resolution as `createPaymentOverridesForGenericCalcs`: that builder resolves a
    // rate per requested term and can give a non-selected column its own priced pair, so a request narrowed
    // past the selected term is answered by `hasPriceableNarrowedColumn` above rather than here.
    const sellRate = offerSelectors.getSellRateOverride(state, false, offerType);
    const sellRateByTerm = offerSelectors.getSellRateOverrideByOfferTypeAndTerm(state, offerType, selectedTermMonths);

    const fields: UPQRequiredFields = { sellRate: sellRateByTerm ?? sellRate };

    // Only the residual *values* are checked, deliberately: failing this guard sets
    // `missingRequiredUPQFields`, and `handleUPQProgramFallback` then re-seeds baseResidualTypeOverride to
    // dollar from the lender's term data — which would discard a percent-based residual the user typed and
    // reprice the deal off the lender's untouched dollar figure. A missing residual *type* is resolved where
    // the payload is built instead, by `pickBaseResidualSource` and `resolveCompleteResidualUnit`.
    if (offerType === LEASE) {
        const leaseInputs = offerSelectors.getOfferDetails(state, LEASE);

        const baseResidualType = leaseInputs.baseResidualTypeOverride;
        fields.baseResidualValue =
            baseResidualType === RESIDUAL_DOLLAR ? leaseInputs.baseResidualAmountOverride : leaseInputs.baseResidualPercentOverride;

        fields.programResidualValue =
            baseResidualType === RESIDUAL_DOLLAR ? leaseInputs.programResidualOverride : leaseInputs.programResidualPercentOverride;

        const adjustedResidualType = leaseInputs.adjustedResidualTypeOverride;
        fields.adjustedResidualValue =
            adjustedResidualType === RESIDUAL_DOLLAR
                ? leaseInputs.adjustedResidualAmountOverride
                : leaseInputs.adjustedResidualPercentOverride;
    }

    if (offerType === BALLOON) {
        fields.balloonPayment = offerSelectors.getOfferDetails(state, BALLOON).balloonPaymentAmountOverride;
    }

    return hasRequiredUPQFields(fields, offerType);
};

export const generateTermId = (
    offerType: OfferType,
    termIdParams: AutoTermIdParams,
    autoTermId?: GetAutoTermId
): { termId: string; mappedTermId: string } => {
    const { months, downpayment } = termIdParams;

    const termId = autoTermId ? autoTermId(termIdParams, true) : offerSelectors.getTermId(offerType, months, downpayment)!;
    const mappedTermId = autoTermId ? autoTermId(termIdParams, false) : offerSelectors.getTermId(offerType, months, downpayment)!;

    return { termId, mappedTermId };
};

export const mapTermsForOffer = (
    offerPayments: ITerm[],
    offerType: 'lease' | 'finance' | 'balloon',
    autoTermId?: GetAutoTermId,
    options: {
        sourceApplication?: string;
        isDueAtSigninAmountDeduct?: boolean;
    } = {}
) => {
    const mappings: { terms: GenericObject } = {
        terms: {}
    };

    const terms = getDefaultTerms();

    const toTermType = () => {
        const termType = `${offerType}Terms`;
        const filteredList = (terms as any)[termType];

        return (payment: any) => {
            const conditionals: SlimTermsIncentives = [];
            const nonConditionals: SlimTermsIncentives = [];
            const appliedConditionalIds: string[] = [];
            const appliedNonConditionalIds: string[] = [];

            const paymentTerm: ITerm = payment.term;
            delete payment.term;
            const term: ITerm = { ...payment, ...paymentTerm };

            term.incentives.conditionals.forEach((incentive) => {
                conditionals.push({
                    id: `conditional_${offerType}_${incentive.id}`,
                    amount: incentive.amount
                });
            });

            term.incentives.nonConditionals.forEach((incentive) => {
                nonConditionals.push({
                    id: `nonconditional_${offerType}_${incentive.id}`,
                    amount: incentive.amount
                });
            });

            term.incentives.conditionals = conditionals;
            term.incentives.nonConditionals = nonConditionals;

            term.appliedIncentives.conditionals.forEach((id) => {
                appliedConditionalIds.push(`conditional_${offerType}_${id}`);
            });

            term.appliedIncentives.nonConditionals.forEach((id) => {
                appliedNonConditionalIds.push(`nonconditional_${offerType}_${id}`);
            });

            term.appliedIncentives.conditionals = appliedConditionalIds;
            term.appliedIncentives.nonConditionals = appliedNonConditionalIds;

            const useResolutionLogicForDownpaymentForSP = shouldUseResolutionLogicForDownPaymentForSP(offerType, options);

            const downPayment = getDownPaymentForOfferType(offerType, term, {
                useResolutionLogic: useResolutionLogicForDownpaymentForSP
            });

            const { termId, mappedTermId } = generateTermId(
                offerType,
                { months: term.months, downpayment: downPayment, fordflexbuy: term.initialPaymentDiscount },
                autoTermId
            );

            term.id = termId;
            mappings.terms[mappedTermId] = term.id;

            terms.byId![term.id] = term;
            terms.allIds.push(term.id);
            filteredList.push(term.id);
        };
    };

    offerPayments.forEach(toTermType());

    return { ...terms, mappings: mappings.terms };
};

export function parseDmvFees(payment: any) {
    if (payment?.fees && payment?.fees?.dmvFees) {
        return payment.fees.dmvFees.total;
    } else if (payment?.fees) {
        return payment?.fees?.total;
    } else {
        return 0;
    }
}

export function transformCashPayment(source: ICashResponse | undefined): CashTransformed {
    let dealerFeesTotal = 0;
    const dmvFees = parseDmvFees(source);
    if (source?.dealerFees && source?.dealerFees.length > 0) {
        dealerFeesTotal = source.dealerFees
            .map((fee: any) => fee.dealerFeeAmount || 0)
            .reduce((current: any, next: any) => current + next);
    }

    const conditionalIncentives = source?.incentives?.conditional;
    const nonConditionalIncentives = source?.incentives?.nonConditional;
    const conditionalIncentivesResults = transformIncentives(conditionalIncentives);
    const nonConditionalIncentivesResults = transformIncentives(nonConditionalIncentives);

    const incentivesToAdd = {
        conditionals: conditionalIncentivesResults.incentivesToAdd,
        nonConditionals: nonConditionalIncentivesResults.incentivesToAdd
    };

    const cashObject = {
        ...cloneDeep(source),
        dmvFees,
        dealerFeesTotal,
        incentives: incentivesToAdd
    };

    return {
        ...cashObject,
        success: !!source
    };
}

export const transformPayments = (params: {
    source: any;
    requestedTermMonths?: number[];
    requestedDownPayment?: number;
}): IPaymentObjectPayload => {
    const { source, requestedDownPayment, requestedTermMonths } = params;
    let isRequestedTermInPayments = true;
    const payments = source?.payments;

    if (!payments || payments.length === 0) {
        return {
            success: false
        };
    }
    const conditionalIncentives = source?.incentives?.conditional;
    const nonConditionalIncentives = source?.incentives?.nonConditional;
    const conditionalIncentivesResults = transformIncentives(conditionalIncentives);
    const nonConditionalIncentivesResults = transformIncentives(nonConditionalIncentives);

    const incentivesToAdd = {
        conditionals: conditionalIncentivesResults.incentivesToAdd,
        nonConditionals: nonConditionalIncentivesResults.incentivesToAdd
    };

    const paymentsToAdd: GenericObject[] = [];
    payments.forEach((sourcePayment: any) => {
        let dealerFeesTotal = 0;
        if (sourcePayment.dealerFees && sourcePayment.dealerFees.length > 0) {
            dealerFeesTotal = sourcePayment.dealerFees
                .map((fee: any) => fee.dealerFeeAmount || 0)
                .reduce((current: any, next: any) => current + next);
        }
        const targetPayment = {
            term: {
                months: sourcePayment.term,
                apr: sourcePayment.apr
            },
            // TODO: pencil service should return monthlyPaymentRounded so it can be used in the ecosystem
            monthlyPaymentRounded: Math.ceil(sourcePayment.monthlyPayment),
            dealerFeesTotal,
            dmvFees: parseDmvFees(source),
            totalTax: sourcePayment.totalTax,
            incentives: {
                conditionals: transformIncentivesForPayment(sourcePayment.term, conditionalIncentives),
                nonConditionals: transformIncentivesForPayment(sourcePayment.term, nonConditionalIncentives)
            },
            appliedIncentives: {
                conditionals: transformAppliedIcentivesForPayment(
                    sourcePayment.term,
                    conditionalIncentivesResults.incentivesLookup,
                    sourcePayment.appliedIncentiveIds
                ),
                nonConditionals: transformAppliedIcentivesForPayment(
                    sourcePayment.term,
                    nonConditionalIncentivesResults.incentivesLookup,
                    sourcePayment.appliedIncentiveIds
                )
            }
        };

        paymentsToAdd.push({ ...sourcePayment, ...targetPayment });
    });

    // checks if all the requested term are in the payments list
    if (requestedTermMonths && requestedTermMonths.length > 0) {
        isRequestedTermInPayments = requestedTermMonths.every((term) =>
            paymentsToAdd.some((payment) => payment.term?.months === term)
        );
    }

    // check is requestedDownPayment is less than due at signing for at least one payment and return false if it is
    // also check if isRequestedTermInPayments is already false, if it is, don't check the down payment
    if (requestedDownPayment && isRequestedTermInPayments) {
        isRequestedTermInPayments = !paymentsToAdd.some((payment) => requestedDownPayment < payment.dueAtSigning);
    }

    const paymentObject: IPaymentObjectPayload = {
        incentives: incentivesToAdd,
        payments: paymentsToAdd,
        success: true,
        isRequestedTermInPayments
    };

    return paymentObject;
};

export function transformPaymentResponse(source: any, currentOfferType: any, paymentRequestOverride?: IPaymentOverride) {
    if (!source) {
        return {
            finance: {
                success: false
            },
            lease: {
                success: false
            },
            cash: {
                success: false
            },
            balloon: {
                success: false
            }
        };
    }
    const target: any = {};
    // handle cash payment transformations differently than lease and finance
    if (currentOfferType === CASH) {
        target[CASH] = transformCashPayment(source.cash);
    } else {
        const overridesFinance = paymentRequestOverride?.finance as IPaymentOverrideFinance & Record<'percents', number[]>;
        const overridesLease = paymentRequestOverride?.lease;
        const overridesBalloon = paymentRequestOverride?.balloon;

        target[FINANCE] = transformPayments({
            source: source.finance,
            requestedTermMonths: overridesFinance?.termList,
            requestedDownPayment: overridesFinance?.downPayment
        });

        target[LEASE] = transformPayments({
            source: source.lease,
            requestedTermMonths: overridesLease?.termList,
            requestedDownPayment: overridesLease?.downPayment
        });
        target[BALLOON] = transformPayments({
            source: source.balloon,
            requestedTermMonths: overridesBalloon?.termList,
            requestedDownPayment: overridesBalloon?.downPayment
        });
    }
    if (source[OFFER_ENCRYPTION_FIELDS.PAYMENT]) {
        target[OFFER_ENCRYPTION_FIELDS.PAYMENT] = source[OFFER_ENCRYPTION_FIELDS.PAYMENT];
    }
    return target;
}

export function getCreditScore(creditTier: any) {
    // The selected credit tier may not resolve to a tier with a score band (e.g. a lease
    // offer referencing a tier absent from the loaded credit-tier list). Guard against the
    // missing band so the payment payload build doesn't throw during dispatch (DE753177).
    // Return -1, the Payment Service "no scoring available" (see payment-api
    // calcpayment_to_soap.xslt): PS maps -1 to its default rather than a literal score.
    // Do NOT return 0 — PS reads 0 as a real score and skews tier/rate selection.
    const score = creditTier?.score;
    if (!score) return -1;

    const isLowestTier = score.low === 0;
    const tierMedianScore = Math.ceil((score.high + score.low) / 2);

    return isLowestTier ? score.high : tierMedianScore;
}

const formatQuestion = (question: Question): FormattedQuestion => {
    const formattedQuestion: any = cloneDeep(question);
    const questionOptions = question.options;

    if (Array.isArray(questionOptions)) {
        formattedQuestion.options = questionOptions.map((option) => ({
            displayValue: option.description,
            id: option.value
        }));
    }

    return formattedQuestion;
};

export const reformatQuestions = (questionsList: Question[]) => {
    const filteredQuestions: FormattedQuestion[] = [];

    for (const question in questionsList) {
        if (has(questionsList, question)) {
            const questionObj = questionsList[question];
            filteredQuestions.push(formatQuestion(questionObj));
        }
    }

    return filteredQuestions;
};
export const exists = (el: any) => el != null;

// Moved to `lenderProgramUtils`, a leaf, so `paymentGridSelectors` can use it without a cycle. Re-exported
// here because a dozen call sites import it from this module.
export { isValidProgram };

/**
 * Whether any term this call actually names has a program to pin, so the deal's current term having none does not
 * suppress an overrides node that carries every other column's pin, residual and rate. Grid calls only, and only while
 * the grid is open: a deal-screen, roll-to or template payload names the deal's own term and nothing else, and an
 * emptied cache entry there is a deliberate "send no program" signal that must not be resurrected.
 */
const hasProgramForRequestedGridTerm = (
    state: GenericObject,
    offerType: OfferType,
    action: AnyFSA,
    options: RequestOverrideOptions
): boolean => {
    if (!action?.meta?.paymentGrid?.gridAction) return false;
    // The caller nulls `program` for this flag; re-reading the cache here would reintroduce what it asked to disregard.
    if (options.skipProgramOverride) return false;
    const isPerColumnProgramPinActive =
        offerSelectors.isAspPaymentGridRedesignActive(state, offerType) ||
        offerSelectors.isManualRebatePreservationActive(state, offerType);
    if (!isPerColumnProgramPinActive) return false;
    if (!offerSelectors.getPaymentGridToggle(state, offerType)) return false;

    const requestedTerms = options.termList;
    if (!requestedTerms?.length) return false;

    return requestedTerms.some(
        (months) =>
            isValidProgram(offerSelectors.getGridColumnProgramInfo(state, offerType, months)) ||
            isValidProgram(options.restoredColumnPrograms?.[months])
    );
};

const resolveProgramForRequest = (
    state: GenericObject,
    offerType: OfferType,
    months: number | undefined,
    options: RequestOverrideOptions
): any => {
    if (isValidProgram(options.lockedProgram)) return options.lockedProgram;
    return offerSelectors.getGridColumnProgramInfo(state, offerType, months);
};

export const doOverridesExistForOfferType = (
    state: GenericObject,
    offerType: OfferType,
    action: AnyFSA,
    options: RequestOverrideOptions = {}
): boolean => {
    // Skip program check when skipProgramOverride is true (e.g., OPL auto-select lender)
    const program: any = options.skipProgramOverride ? null : resolveProgramForRequest(state, offerType, undefined, options);
    const validProgram = isValidProgram(program);
    const hasGridTermProgram = validProgram ? false : hasProgramForRequestedGridTerm(state, offerType, action, options);
    // When skipProgramOverride is true, we don't require a valid program
    if (!validProgram && !options.useGenericCalcs && !options.skipProgramOverride) {
        // Still written, and still truthful: the deal's own term has no program. Left in place so every downstream reader
        // behaves as before; only the suppression below is narrowed.
        action.meta = {
            ...action.meta,
            missingRequiredPSFields: true
        };
        if (!hasGridTermProgram) return false;
    }
    const offerDetails = offerSelectors.getOfferDetails(state, offerType);
    const {
        acqFeeOverride,
        acqFeeUpFrontOverride,
        securityDepositOverride,
        retailPriceOverride,
        overridesByTerm,
        manualIncentives
    } = offerDetails;

    return [
        acqFeeOverride,
        acqFeeUpFrontOverride,
        securityDepositOverride,
        retailPriceOverride,
        // US2075533: not the map itself. Every priced term now carries the rate type its column is labelled
        // with, so the map is populated on deals the user never overrode anything on — and its bare presence
        // would make this report overrides that do not exist.
        hasUserTermOverrides(overridesByTerm) || null,
        manualIncentives,
        // Skip program when skipProgramOverride is true (OPL auto-select lender)
        options.skipProgramOverride ? null : program,
        // A grid row whose current column has no program still has per-term pins worth sending.
        hasGridTermProgram ? true : null
    ].some(exists);
};

const isRequestManualRebate = (incentive: ManualIncentiveEntry, byProvenance: boolean): boolean =>
    byProvenance ? isManualIncentiveByProvenance(incentive) : isManualRebateForRequest(incentive);

const sumUserManualIncentives = (incentives: ManualIncentiveEntry[], byProvenance: boolean): number =>
    roundToTwoDecimal(
        incentives
            .filter((incentive) => isRequestManualRebate(incentive, byProvenance))
            .reduce((sum, incentive) => sum + (Number(incentive.amount) || 0), 0)
    );

// DE783585: cash rows never carry `conditionCategory`, so the field-keyed classifier cannot tell a program
// conditional from a dealer-entered CustomerCash rebate and sums it into an amount channel the request
// already names by id. Both callers flip together for cash deliberately - `dealerCash` carried the same
// duplication, since MANUAL_INCENTIVE_TYPES covers DealerCash too.
const isManualByProvenanceForRequest = (state: Record<string, GenericObject>): boolean => {
    const offerType = offerSelectors.getCurrentOfferType(state);
    if (offerType === CASH) return isCashManualRebateProvenanceEnabled(state);
    return isGridIncentiveManagementEnabled(state, offerType);
};

const shouldSendOnlyCustomDealerCash = (state: Record<string, GenericObject>, options: RequestOverrideOptions): boolean =>
    Boolean(options.isManagerView) && !options.useGenericCalcs && isProgramIncentivesRestorationEnabled(state);

const getDealerCashForRequest = (state: Record<string, GenericObject>, options: RequestOverrideOptions): number =>
    shouldSendOnlyCustomDealerCash(state, options)
        ? sumUserManualIncentives(
              offerSelectors.getAppliedIncentivesForDealerCash(state) ?? [],
              isManualByProvenanceForRequest(state)
          )
        : offerSelectors.getAppliedIncentivesTotalForDealerCash(state);

const leavesDealerStandardsOut = (state: GenericObject, offerType: OfferType, isManagerView?: boolean): boolean =>
    !isManagerView && offerSelectors.shouldHonorDealerStandardExclusion(state, offerType);

const getExcludedStandardFilter = (
    state: GenericObject,
    offerType: OfferType,
    isManagerView?: boolean
): ((incentive: ManualIncentiveEntry) => boolean) =>
    leavesDealerStandardsOut(state, offerType, isManagerView)
        ? offerSelectors.getExcludedStandardPredicate(state, offerType)
        : () => false;

const getGenericCalcsRebates = (state: GenericObject, offerType: OfferType, isManagerView?: boolean): number =>
    leavesDealerStandardsOut(state, offerType, isManagerView)
        ? offerSelectors.getAppliedIncentivesTotalForNonDealerCashWithoutStandards(state, offerType)
        : offerSelectors.getAppliedIncentivesTotalForNonDealerCash(state, offerType);

/**
 * True when `byOfferType[offerType]` exists — the exact condition that determines whether
 * `getAppliedIncentivesTotalForNonDealerCash` / `getAppliedIncentivesTotalForDealerCash` can be called
 * safely for this offer type. Both dereference `byOfferType[offerType].term` transitively, through
 * `getTermMonthByOfferTypeFromOffer` (`selectors/slices/offerSliceSelectors.ts`), behind a compile-time-only
 * non-null assertion (`byOfferType!`) — an offer type that was never set up (`byOfferType[offerType]`
 * undefined) throws there at runtime, not merely misses a lookup. `getOfferDetails` itself can never throw
 * (`getOfferDetailsByOfferType` falls back to `{}`), so it's specifically this deeper call that's the risk.
 *
 * Deliberately checks entry existence rather than `getOfferDetails(state, offerType).term != null`: the
 * latter is also false when the entry EXISTS but `term` happens to be unset, which isn't the crash case and
 * would skip a total-selector call that's perfectly safe to make.
 */
const hasOfferTypeInputs = (state: Record<string, GenericObject>, offerType?: OfferType): boolean => {
    const targetOfferType = offerType || offerSelectors.getCurrentOfferType(state);
    return offerSelectors.getPenciledInputs(state).byOfferType?.[targetOfferType] != null;
};

const getManualIncentivesRequestNode = (
    state: Record<string, GenericObject>,
    options: RequestOverrideOptions
): ManualIncentivesRequest | undefined => {
    // No explicit offerType here, matching the other no-arg selector calls in this function
    // (getManualIncentives, hasOfferTypeInputs below) — this builder always operates on the CURRENT
    // offer type, resolved internally by each selector via getCurrentOfferType. Deliberate, not an
    // oversight relative to the sibling builder below, which is always given an explicit offerType.
    const manualIncentives = offerSelectors.getManualIncentives(state);
    // Program calls historically dropped manual incentives. Keep that on the restoration-OFF path,
    // but include them when restoration is ON so grid quotes match the applied-program payment.
    const excludeManualIncentives = options.isProgramCall && !isProgramIncentivesRestorationEnabled(state);

    if (excludeManualIncentives) {
        return undefined;
    }
    // A payment-grid checkbox toggle can delete the manual-incentive bag while a rebate is still applied. On
    // the generic path Payment Services reads the amount from manualIncentives.rebates only, so gating on the
    // bag there silently drops the still-applied rebate. Fall through when the offer type still has penciled
    // inputs; the non-generic paths keep the bag gate, since they resolve program incentives by id rather
    // than by amount. See hasOfferTypeInputs for why "has inputs" means "the byOfferType entry exists", not
    // "term is set" — the two aren't the same condition, and only the first is what the generic total
    // selectors below actually need to be safe.
    const offerTypeHasInputs = hasOfferTypeInputs(state);
    if (!manualIncentives && !(options.useGenericCalcs && offerTypeHasInputs)) {
        return undefined;
    }
    const manualIncentivesRequest = {} as ManualIncentivesRequest;
    const dealerCash = getDealerCashForRequest(state, options);

    if (dealerCash) {
        manualIncentivesRequest.dealerCash = dealerCash;
    }

    if (options.useGenericCalcs) {
        // Sends the rebate as an amount rather than as ids, and Payment Services treats that amount as
        // authoritative. On this path the dealer has turned Auto Select Program off, so the amount they
        // applied is deliberately carried across a term change rather than re-derived — see the matching
        // gate in rederiveManualIncentivesForTerm (DE760840). Ids are not an alternative here: PS does not
        // resolve incentives by id on the generic path (see buildAppliedIncentiveIds below).
        manualIncentivesRequest.rebates = getGenericCalcsRebates(
            state,
            offerSelectors.getCurrentOfferType(state),
            options.isManagerView
        );
    } else if (isProgramIncentivesRestorationEnabled(state)) {
        const isExcludedStandard = getExcludedStandardFilter(
            state,
            offerSelectors.getCurrentOfferType(state),
            options.isManagerView
        );
        const manualRebateTotal = sumUserManualIncentives(
            (offerSelectors.getAppliedIncentivesForNonDealerCash(state) ?? []).filter(
                (incentive) => !isExcludedStandard(incentive)
            ),
            isManualByProvenanceForRequest(state)
        );
        if (manualRebateTotal) {
            manualIncentivesRequest.rebates = manualRebateTotal;
        }
    } else {
        const rebates = offerSelectors.getAppliedIncentivesForNonDealerCash(state) ?? [];
        const manualRebateTotal = rebates.filter((rebate) => rebate.manual).reduce((sum, rebate) => sum + rebate.amount, 0);
        if (manualRebateTotal) {
            manualIncentivesRequest.rebates = roundToTwoDecimal(manualRebateTotal);
        }
        const programIncentiveIds = rebates
            .filter((rebate) => !rebate.manual && rebate.code !== undefined)
            .map((rebate) => rebate.code as number);
        if (programIncentiveIds.length) {
            manualIncentivesRequest.incentiveIds = programIncentiveIds;
        }
    }
    return manualIncentivesRequest;
};

/**
 * Builds the applied-incentive id list sent on the generic (UPQ) path, where Payment
 * Services does not resolve incentives by id. The orchestrator echoes these ids back as
 * appliedIncentiveIds and pairs them with the rebate already in the payload overrides as
 * appliedIncentiveAmount, so we send only the ids here — derived from the same
 * applied-incentive list (getAppliedIncentivesWithFilter, dealer cash kept) the caller
 * already summed into those overrides, with no second source of truth to drift against.
 */
export const buildAppliedIncentiveIds = (
    state: GenericObject,
    offerType: OfferType,
    isManagerView?: boolean
): number[] | undefined => {
    const isExcludedStandard = getExcludedStandardFilter(state, offerType, isManagerView);
    const appliedIncentiveIds = Array.from(
        new Set(
            offerSelectors
                .getAppliedIncentivesWithFilter(state, offerType)
                .filter((entry: ManualIncentiveEntry) => !isExcludedStandard(entry))
                .map((entry: ManualIncentiveEntry) => entry.code)
                .filter((code): code is number => code != null)
        )
    );

    return appliedIncentiveIds.length ? appliedIncentiveIds : undefined;
};

/**
 * Payment Services accepts the user-supplied baseResidual values and returns the adjustedResidual values; those
 * are then read-only, so MV does not supply them.
 */
const skipAdjustedResidualValuesFor = (state: GenericObject, options: RequestOverrideOptions): boolean =>
    !!options.isManagerView && !!state.featureToggles?.enableLeaseResidualTreatment_F286227;

/**
 * The residual fields a payload term carries, read off a residual record's own type. One mapping, shared by the
 * two passes that write residuals per term - they differ in which terms they touch and whether they may
 * overwrite, not in how a residual becomes payload fields.
 */
const termResidualFieldsFrom = (residual: GenericObject, skipAdjustedResidualValues: boolean): GenericObject => {
    const baseResidualType = residual.baseResidualType;
    const isDollar = baseResidualType === RESIDUAL_DOLLAR;
    const adjustedResidualType = residual.adjustedResidualType;

    return {
        baseResidualType,
        baseResidualValue: isDollar ? residual.baseResidualAmount : residual.baseResidualPercent,
        programResidualValue: isDollar ? residual.programResidual : residual.programResidualPercent,
        ...(!skipAdjustedResidualValues && {
            adjustedResidualType,
            adjustedResidualValue:
                adjustedResidualType === RESIDUAL_DOLLAR ? residual.adjustedResidualAmount : residual.adjustedResidualPercent
        })
    };
};

/**
 * Writes a residual record onto one payload term. `skipUndefined` is the whole difference between the callers:
 * the per-term pass fills gaps and must not blank a value the deal-level block already supplied, while the
 * grid-column pass owns the terms its call names outright.
 */
const writeTermResidual = (
    termOverrides: GenericObject,
    residual: GenericObject,
    { skipAdjustedResidualValues, skipUndefined }: { skipAdjustedResidualValues: boolean; skipUndefined: boolean }
): void => {
    Object.entries(termResidualFieldsFrom(residual, skipAdjustedResidualValues)).forEach(([field, value]) => {
        if (skipUndefined && value == null) return;
        termOverrides[field] = value;
    });
};

/**
 * Whether a Maximum Residualized MSRP is a real lender cap worth sending.
 *
 * `0` is not a $0 cap - it is "no cap known". It is what a user-program-quotes response reports, because on that
 * engine the caller owns the residual and no lender cap is resolved. `penciledInputsStateUtils`' step 9, "Sync MRM
 * overrides from PO response", copies it onto the deal's own `maxResidualizedMsrp`, so after any ASP-off Lease
 * payment the deal holds `0` and the deal screen's MRM field reads $0.00.
 *
 * Sent, it is destructive rather than inert. The payment orchestrator forwards it verbatim -
 * `programQuotes/request.ts`: `if (isNumber(maxResidualizedMsrp)) selectedProgramOverrides.maxResidualizedMsrp = ...`,
 * and `isNumber(0)` is true - so Payment Services residualizes every term against a $0 MSRP and returns an empty
 * quote list, blanking the whole grid.
 *
 * Behind the redesign gate so every toggle-off payload stays byte-identical. The same fabricated `0` can reach a
 * toggle-off Lease program-quotes payload; that is pre-existing master behaviour.
 */
const shouldSendMaxResidualizedMsrp = (state: GenericObject, offerType: OfferType, maxResidualizedMsrp?: number): boolean => {
    if (maxResidualizedMsrp == null) return false;
    if (offerType !== LEASE || !offerSelectors.isAspPaymentGridRedesignActive(state, offerType)) return true;
    return maxResidualizedMsrp > 0;
};

/**
 * A Finance grid column call forced onto user-program-quotes: a column's fallback, or the forced half of a split row.
 * One gate for the three seams that treat it differently - rate, program, incentives - so they cannot drift apart.
 */
const isFinanceGridForcedColumnCall = (state: GenericObject, offerType: OfferType, options: RequestOverrideOptions): boolean =>
    offerSelectors.isAspPaymentGridRedesignActive(state, offerType) &&
    offerType === FINANCE &&
    Boolean(offerSelectors.getPaymentGridToggle(state, offerType)) &&
    Boolean(options.forceUseGenericCalcs) &&
    Boolean(options.termList?.length);

/**
 * The rate the active cell is priced at, without `getSellRateByOfferType`'s `?? 0`: a term with no record means
 * absent, not a chosen 0% APR. A record that genuinely says 0 is still sent.
 */
const resolveActiveCellSellRate = (
    state: GenericObject,
    offerType: OfferType,
    currentTerm: number | undefined
): number | undefined => {
    const override = offerSelectors.getSellRateOverrideByOfferTypeAndTerm(state, offerType, currentTerm);
    if (override != null) return override;

    const activeTerm = currentTerm
        ? offerSelectors.getTermByOfferTypeAndMonths(state, offerType, currentTerm)
        : offerSelectors.getSelectedTermByOfferType(state, offerType);

    return activeTerm?.sellRate;
};

const resolveRequestedColumn = (
    state: GenericObject,
    offerType: OfferType,
    months: number,
    options: RequestOverrideOptions
): number | undefined => {
    if (!options.gridColumnIndices?.length) return undefined;

    const gridTerms = offerSelectors.getPaymentGridTerms(state, offerType);
    const columns = options.gridColumnIndices.filter((columnIndex) => gridTerms[columnIndex] === months);
    return columns.length === 1 ? columns[0] : undefined;
};

const getColumnOwnRateType = (
    state: GenericObject,
    offerType: OfferType,
    months: number,
    options: RequestOverrideOptions,
    isColumnSellRate: boolean
): string | undefined => {
    if (offerType !== LEASE) return undefined;

    const columnIndex = resolveRequestedColumn(state, offerType, months, options);
    if (columnIndex == null) return undefined;
    if (!isColumnSellRate) return offerSelectors.getPaymentGridTermRateType(state, offerType, months);
    if (offerSelectors.getOwnColumnOverride(state, offerType, columnIndex, months)?.sellRate == null) return undefined;

    return offerSelectors.getPaymentGridColumnRateType(state, offerType, columnIndex);
};

const sendsDuplicatedColumnOwnRate = (
    state: GenericObject,
    offerType: OfferType,
    months: number,
    options: RequestOverrideOptions
): boolean => {
    if (offerType !== LEASE || !state.featureToggles?.F298789_ASP_PG_redesign || options.columnSellRate == null) return false;

    const columnIndex = resolveRequestedColumn(state, offerType, months, options);
    if (columnIndex == null || columnIndex === offerSelectors.getPaymentGridTermOwnerColumnIndex(state, offerType)) {
        return false;
    }

    return (
        offerSelectors.isTermDuplicated(offerSelectors.getPaymentGridTerms(state, offerType), months) &&
        offerSelectors.getOwnColumnOverride(state, offerType, columnIndex, months)?.sellRate != null
    );
};

const resolveRequestRateType = (
    state: GenericObject,
    offerType: OfferType,
    months: number,
    options: RequestOverrideOptions,
    isColumnSellRate = false
): string | undefined =>
    getColumnOwnRateType(state, offerType, months, options, isColumnSellRate) ??
    (state.offer?.terms ? offerSelectors.getRateTypeByTerm(state, offerType, months) : undefined);

const isSendableSellRate = (
    state: GenericObject,
    offerType: OfferType,
    months: number,
    sellRate: number | undefined,
    options: RequestOverrideOptions,
    isColumnSellRate = false
): boolean => {
    if (sellRate == null) return false;
    if (offerType !== LEASE || !exceedsMoneyFactorBound(sellRate)) return true;

    return !isImplausibleMoneyFactor(resolveRequestRateType(state, offerType, months, options, isColumnSellRate), sellRate);
};

const isGridColumnRequest = (options: RequestOverrideOptions): boolean =>
    options.columnSellRate != null ||
    options.ignoreTermSellRateOverride === true ||
    options.gridColumnIndex != null ||
    Boolean(options.gridColumnIndices?.length);

/**
 * The sell rate that travels with a typed buy rate: the user's own, else the priced term's, but only while that priced
 * rate came from the program now being requested. After a program change the old rate would pin the new program.
 */
const sellRateForBuyRatePair = (
    sellRateOverride: number | undefined,
    pricedTerm: GenericObject | undefined,
    program: GenericObject | null | undefined
): number | undefined =>
    sellRateOverride ?? (isSameLenderProgram(pricedTerm?.lenderProgram, program) ? pricedTerm?.sellRate : undefined);

/**
 * A typed lease buy rate for a program-quotes payload. The engine defaults an absent sell rate to the buy rate, so the
 * two always travel together, and only for the selected term on the deal screen.
 */
const programQuotesBuyRatePair = (
    state: GenericObject,
    offerType: OfferType,
    months: number,
    sellRateOverride: number | undefined,
    program: GenericObject | null | undefined,
    options: RequestOverrideOptions
): { sellRate: number; buyRate: number } | undefined => {
    if (offerType !== LEASE || !state.featureToggles?.DE773860_Enable_Buy_Rate_PQ_First) return undefined;
    if (isGridColumnRequest(options) || offerSelectors.getSelectedTermMonths(state, offerType) !== months) return undefined;

    const buyRate = offerSelectors.getTypedBuyRateOverrideByOfferTypeAndTerm(state, offerType, months);
    const sellRate = sellRateForBuyRatePair(
        sellRateOverride,
        offerSelectors.getTermByOfferTypeAndMonths(state, offerType, months),
        program
    );
    if (buyRate == null || sellRate == null || !isSendableSellRate(state, offerType, months, sellRate, options)) return undefined;
    if (isImplausibleMoneyFactor(resolveRequestRateType(state, offerType, months, options), sellRate, buyRate)) return undefined;
    return { sellRate, buyRate };
};

export function createPaymentOverrides(
    state: GenericObject,
    terms: number[],
    offerType: OfferType,
    options: RequestOverrideOptions = {}
): GenericObject {
    const acqFee = state.featureToggles?.enableLenderFeeEdits
        ? null
        : offerSelectors.getAcqFeeOverrideByOfferType(state, offerType);
    const retailPrice = offerSelectors.getRetailPriceOverrideByOfferType(state, offerType);
    const acqFeeUpFront = state.featureToggles?.enableLenderFeeEdits
        ? null
        : offerSelectors.getAcqFeeUpFrontOverrideByOfferType(state, offerType);
    const securityDeposit = offerSelectors.getSecurityDepositOverrideByOfferType(state, offerType);
    const { maxResidualizedMsrp } = resolveMaxResidualizedMsrpPair(state, offerType);
    const maxResidualizedMsrpApplied = offerSelectors.getMaxResidualizedMsrpAppliedOverrideByOfferType(state, offerType);
    const manualIncentivesRequest = getManualIncentivesRequestNode(state, options);

    const offerOverrides: GenericObject = {
        ...(retailPrice != null ? { retailPrice } : undefined),
        ...(acqFeeUpFront != null ? { acqFeeUpFront } : undefined)
    };

    if (manualIncentivesRequest) offerOverrides.manualIncentives = manualIncentivesRequest;

    const termOverrides: Record<number, any> = {};

    if (options.nonSelectedTermHonoringSV) {
        const selectedTermSellRateOverride = offerSelectors.getSellRateOverride(state);
        const selectedTermMonths = offerSelectors.getSelectedTermMonths(state, offerType);

        for (const months of terms) {
            // Skip program override when skipProgramOverride is true (e.g., OPL auto-select lender)
            const shouldSkipProgram =
                options.skipProgramOverride || (!options.isExplicitProgramLock && getIsShopperUpdateFailed(state));
            // Resolved, not read raw: the current-term column's program lives on the deal's selected term when the
            // per-term cache has been emptied by a deal-screen action.
            const program = shouldSkipProgram ? null : resolveProgramForRequest(state, offerType, months, options);
            const sellRateOverrideForTerm = offerSelectors.getSellRateOverrideByOfferTypeAndTerm(state, offerType, months);
            // Program-quotes prices from a supplied rate instead of the pinned program's rate card, so the deal's
            // own rate may back its own term and no other. Lease was always excluded; F311786 extends it to finance.
            const mayUseDealRate = offerType !== LEASE && (!options.upqFallbackDisabled || months === selectedTermMonths);
            const sellRateOverride = sellRateOverrideForTerm ?? (mayUseDealRate ? selectedTermSellRateOverride : undefined);

            const maySendSellRate = isSendableSellRate(state, offerType, months, sellRateOverride, options);

            if (isValidProgram(program)) {
                termOverrides[months] = {
                    program,
                    ...(maySendSellRate ? { sellRate: sellRateOverride } : undefined)
                };
            } else if (maySendSellRate) {
                // Preserve sell rate override even when program is invalid (DE730661)
                termOverrides[months] = { sellRate: sellRateOverride };
            }
        }
        if (Object.keys(termOverrides).length > 0) offerOverrides.byTerm = termOverrides;
        return offerOverrides;
    }

    offerOverrides.acqFee = acqFee != null ? acqFee : undefined;
    offerOverrides.securityDeposit = securityDeposit != null ? securityDeposit : undefined;

    const isFlexBuyPayment = offerSelectors.isFlexBuyPayment(state);
    const percents = (options as any).percents;
    const percentsLength = percents?.length || 0;

    const initialPaymentDiscount =
        isFlexBuyPayment && offerType === FINANCE ? offerSelectors.getOfferInitialPaymentDiscount(state) : null;

    const skipAdjustedResidualValues = skipAdjustedResidualValuesFor(state, options);

    terms.forEach((months, index) => {
        // Skip program override when skipProgramOverride is true (e.g., OPL auto-select lender)
        const shouldSkipProgram =
            options.skipProgramOverride || (!options.isExplicitProgramLock && getIsShopperUpdateFailed(state));
        // Resolved, not read raw: see the note in the nonSelectedTermHonoringSV branch above.
        const restoredProgram = options.restoredColumnPrograms?.[months];
        const resolvedProgram = shouldSkipProgram ? null : resolveProgramForRequest(state, offerType, months, options);
        const program =
            shouldSkipProgram || isValidProgram(options.lockedProgram) || !restoredProgram ? resolvedProgram : restoredProgram;
        const sellRate =
            options.columnSellRate ??
            (options.ignoreTermSellRateOverride
                ? undefined
                : offerSelectors.getSellRateOverrideByOfferTypeAndTerm(state, offerType, months));

        const overriddenResidual =
            options.columnResidualInfo ??
            (options.ignoreTermSellRateOverride || options.ignoreTermResidualOverride
                ? ({} as ResidualInfo)
                : offerSelectors.getResidualInfoOverrideByOfferTypeTerm(state, offerType, months) || ({} as ResidualInfo));

        const { adjustedResidualType, adjustedResidualPercent, adjustedResidualAmount } = overriddenResidual;

        const adjustedResidualValue = adjustedResidualType === RESIDUAL_DOLLAR ? adjustedResidualAmount : adjustedResidualPercent;

        // A residual typed into a grid column reaches program-quotes for the same reason the `sellRate` above
        // does: both are the grid's own inputs, and the two cannot disagree about whether this engine honours
        // what the user entered. Without it a residual edit under Auto Select ON wrote `overridesByTerm`
        // correctly and the payload simply never carried it (DE762937).
        // Same complete-pair rule as the generic-calcs builder — type and value from one source, or neither —
        // and behind the redesign toggle, so a toggle-off program-quotes payload is byte-identical.
        const gridResidualUnit = state.featureToggles?.F298789_ASP_PG_redesign
            ? resolveCompleteResidualUnit(overriddenResidual)
            : undefined;
        const isDollarGridResidual = gridResidualUnit === RESIDUAL_DOLLAR;
        const gridBaseResidualValue = isDollarGridResidual
            ? overriddenResidual.baseResidualAmount
            : overriddenResidual.baseResidualPercent;
        const gridProgramResidualValue = isDollarGridResidual
            ? overriddenResidual.programResidual
            : overriddenResidual.programResidualPercent;

        const shouldIncludeProgramOverride =
            program != null &&
            ((typeof options.enableOverridesForSalesView === 'boolean' && !options.enableOverridesForSalesView) ||
                isValidProgram(program));

        termOverrides[months] = {
            ...termOverrides[months],
            ...(shouldIncludeProgramOverride ? { program } : undefined),
            ...(isSendableSellRate(state, offerType, months, sellRate, options, options.columnSellRate != null)
                ? { sellRate }
                : undefined),
            ...programQuotesBuyRatePair(state, offerType, months, sellRate, program, options),
            ...(adjustedResidualType != null && !skipAdjustedResidualValues
                ? { adjustedResidualValue, adjustedResidualType }
                : undefined),
            ...(gridResidualUnit != null
                ? {
                      baseResidualType: gridResidualUnit,
                      ...(gridBaseResidualValue != null && { baseResidualValue: gridBaseResidualValue }),
                      ...(gridProgramResidualValue != null && { programResidualValue: gridProgramResidualValue })
                  }
                : undefined),
            // Filtered once, by `resolveMaxResidualizedMsrpPair` above — re-testing `shouldSendMaxResidualizedMsrp`
            // here is a second guard that cannot disagree with the first.
            ...(maxResidualizedMsrp != null ? { maxResidualizedMsrp } : undefined),
            ...(maxResidualizedMsrpApplied != null ? { maxResidualizedMsrpApplied } : undefined),
            ...(initialPaymentDiscount && !percentsLength ? { initialPaymentDiscount } : undefined)
        };

        const percentAtIndex = percents?.[index];

        if (
            !!percentsLength &&
            percentAtIndex != null &&
            !termOverrides[months]?.initialPaymentDiscount?.includes(percentAtIndex)
        ) {
            termOverrides[months].initialPaymentDiscount = [
                ...(termOverrides[months].initialPaymentDiscount || []),
                percentAtIndex
            ];
        }
    });

    if (Object.keys(termOverrides).length > 0) offerOverrides.byTerm = termOverrides;

    return offerOverrides;
}

export const getPaymentGridOverridesByTerm = (state: GenericObject, offerType: OfferType, options: RequestOverrideOptions) => {
    const currentTerm = offerSelectors.getSelectedTermMonths(state, offerType);
    const buyRate = offerSelectors.getBuyRateOverride(state, false, offerType);
    const sellRate = offerSelectors.getSellRateOverride(state, false, offerType);
    const rateType = offerSelectors.getRateTypeOverrideByOfferType(state, offerType);
    const isPaymentGridEnabled = offerSelectors.getPaymentGridToggle(state, offerType);
    const enablePaymentCallRateTypeFix = state.featureToggles?.enablePaymentCallRateTypeFix;

    const termList = options.termList;
    const months = (termList?.length ? termList : [currentTerm]) as number[];

    const isForcedFinanceGridCall = isFinanceGridForcedColumnCall(state, offerType, options);

    // A forced column has no user-entered rate, so this builder would send none: it falls back to the rate the
    // active cell is priced at. Resolved once here - every term in a forced row is deliberately sent that rate.
    // Only a Finance grid column call drops the coercing read: on every other forced payload - a Lease row, a grid
    // cell selection - a 0 keeps going out as it does today, since withholding it would take the buy rate with it.
    const forcedFallbackSellRate = !options.forceUseGenericCalcs
        ? undefined
        : isForcedFinanceGridCall
        ? resolveActiveCellSellRate(state, offerType, currentTerm)
        : offerSelectors.getSellRateOverrideByOfferTypeAndTerm(state, offerType, currentTerm, true, true);

    /**
     * A Lease grid row forced onto user-program-quotes sends a rate only for the columns that have one of their own.
     *
     * On that engine the supplied rate IS what prices the payment, and both fallbacks below are substitutions:
     * `sellRate` is the deal screen's own override, and `forcedFallbackSellRate` the active column's. Observed on a
     * cash-down row edit, where a blank column with no program and no rate of its own came back priced at the deal's
     * money factor - a payment for a column that was never quoted.
     *
     * Narrow on three axes, so nothing else moves. FORCED calls only: program-quotes keeps its pinned program, which
     * governs the rate there, so every program-quotes payload stays byte-identical. NON-CURRENT terms only: the
     * deal's rate IS the current column's own rate. NAMED terms only: a deal-screen, roll-to or template payload
     * names no terms and is untouched.
     */
    /**
     * A Lease grid column call under the redesign, forced or not. The redesign's per-column rules hang off this:
     * outside it every payload keeps the shape it sends today, including the legacy unit default.
     */
    const isRedesignLeaseGridCall =
        offerSelectors.isAspPaymentGridRedesignActive(state, offerType) &&
        offerType === LEASE &&
        Boolean(isPaymentGridEnabled) &&
        Boolean(termList?.length);

    const isLeaseGridForcedColumnCall =
        // The same gate every other seam in this change routes through, so the payload builder cannot disagree with
        // the grid, the middleware and the components about whether the redesign is in force. A raw toggle read
        // differs from the gate on Flex Buy - `isFlexBuyPayment` reads the FINANCE node regardless of the current
        // offer type, so it is true on a Flex Buy deal viewed as Lease.
        offerSelectors.isAspPaymentGridRedesignActive(state, offerType) &&
        offerType === LEASE &&
        Boolean(isPaymentGridEnabled) &&
        Boolean(options.forceUseGenericCalcs) &&
        Boolean(termList?.length);

    // DE763759: the chain below fell through to the ACTIVE cell's rate for every named column, so a rate typed into
    // one column re-priced the others. A column's own priced rate now wins; the substitution stays the last resort.

    return months.reduce((acc, term) => {
        const rateTypeByTerm = offerSelectors.getRateTypeByTerm(state, offerType, term);

        // need to just get override by term if it exists, and no fallback
        const sellRateByOfferTypeAndTerm = offerSelectors.getSellRateOverrideByOfferTypeAndTerm(
            state,
            offerType,
            term,
            options.isRollToCall
        );
        const buyRateOverrideByOfferTypeAndTerm = offerSelectors.getBuyRateOverrideByOfferTypeAndTerm(
            state,
            offerType,
            term,
            options.isRollToCall
        );

        const notSelectedTermHasBuyRateOverride = buyRateOverrideByOfferTypeAndTerm && currentTerm !== term;
        const useBuyRateOverride = notSelectedTermHasBuyRateOverride || options.isRollToCall;
        const isSelectedTerm = currentTerm === term;

        // The deal-level override is the selected term's rate, so a non-selected column prefers the term it
        // was last priced at — the same figure the grid's Sell Rate row shows for it. Without this, a request
        // narrowed to a single non-active column quotes that column at the selected term's rate: a residual
        // edit narrows the request exactly that way and writes no sell rate of its own (DE762937).
        // Rate and rate type are taken from that one term together. `getRateTypeByTerm` prefers the deal-level
        // `rateTypeOverride` (DE747870, so the grid label matches the penciled value beside it), which is the
        // selected term's unit — pairing it with another term's rate sends a money-factor type against an
        // APR-scaled figure, which Payment Services rejects against the money-factor bound.
        // A column the lender never priced still falls through to the deal-level pair. That rate is not its
        // own, but sending no rate at all is the worse failure: under user-program-quotes Payment Services
        // owns none of these figures and rejects a term that arrives incomplete.
        // DE747870 gives the deal-level type precedence for a non-grid MV lease call. A column priced from its
        // own term must not run inside that path: the rate would be this term's and the type the deal's, which
        // is the mismatch described above with the operands swapped.
        const isDealLevelRateTypePath =
            Boolean(enablePaymentCallRateTypeFix) && Boolean(options.isManagerView) && offerType === LEASE && !isPaymentGridEnabled;

        // Only for the request shape DE762937 reported: one narrowed past the deal's selected term. In a full
        // fan-out the deal-level override is the fresher figure — a user who unticks Auto Select and types a
        // rate on the deal screen must not have every other column quoted at the lender rates an earlier
        // Auto-Select-on build left in `offer.terms`.
        const isRequestWithoutSelectedTerm = !months.includes(currentTerm as number);

        // Rate, unit and buy rate all come off ONE term record, so they cannot describe different things.
        // That record is addressed by months alone, so on a multi-cash-down grid it is whichever row hydrated
        // first. `getSellRateOverrideByOfferTypeAndTerm(..., true, true)` — the codebase's existing expression
        // of "this column's own priced rate" — resolves through the same `getTermByOfferTypeAndMonths`, so it
        // shares the limitation rather than fixing it. Addressing `offer.terms` per cash-down row is a wider
        // change than this defect.
        // Per column for a forced Finance call, not per request: a split row's forced half can name the selected term
        // alongside others, and a request-level test then substituted the active rate into all of them.
        const ownPricedTerm =
            isSelectedTerm || (!isRequestWithoutSelectedTerm && !isForcedFinanceGridCall)
                ? undefined
                : offerSelectors.getTermByOfferTypeAndMonths(state, offerType, term);
        const ownPricedSellRate = ownPricedTerm?.sellRate;
        const ownPricedRateType = ownPricedTerm?.rateType;

        // Behind the redesign toggle: carrying a column's own rate is the payment grid's behaviour, and a
        // toggle-off multi-term lease request (the Alt Deal Structure grid) must keep sending what it sends
        // today.
        // A rate type is required on the Lease clause, not derived: a term can carry a `sellRate` with no
        // `rateType`, and borrowing the deal's unit for it would pair a money-factor type with an APR-scaled
        // figure, the rejection this block exists to prevent.
        const usesOwnPricedRate =
            (Boolean(state.featureToggles?.F298789_ASP_PG_redesign) &&
                offerType === LEASE &&
                !isDealLevelRateTypePath &&
                sellRateByOfferTypeAndTerm == null &&
                ownPricedSellRate != null &&
                ownPricedRateType != null &&
                canClaimOwnPricedRateForTerm(state, offerType, term)) ||
            (isForcedFinanceGridCall && sellRateByOfferTypeAndTerm == null && ownPricedSellRate != null);

        // Unticking Auto Select writes no rate, so on a request narrowed to one column the deal-level override
        // can be `undefined` - and with `forcedFallbackSellRate` set only for forced calls, the chain ended
        // nowhere and the column went out with NO rate and NO buy rate, which Payment Services rejects. The
        // selected term's own priced rate is the last resort: not this column's, but a complete pair beats no
        // quote at all, and it is the figure the deal is being priced with.
        // Scoped to the same narrow case as the per-column rate above: a redesign lease request narrowed past
        // the selected term. Every other payload keeps the shape it sends today.
        const needsCompleteRatePair =
            isRequestWithoutSelectedTerm && Boolean(state.featureToggles?.F298789_ASP_PG_redesign) && offerType === LEASE;
        const lastResortSellRate = needsCompleteRatePair
            ? offerSelectors.getSellRateOverrideByOfferTypeAndTerm(state, offerType, currentTerm, true, true)
            : undefined;
        const lastResortBuyRate = needsCompleteRatePair
            ? offerSelectors.getBuyRateOverrideByOfferTypeAndTerm(state, offerType, currentTerm, true)
            : undefined;

        const sellRateFallback = usesOwnPricedRate ? ownPricedSellRate : sellRate;

        // NOTE US2075533: the payment grid's per-term rate type stamp is deliberately display-only and is NOT
        // read here. A term node carries one `rateType` but its `sellRate` and `buyRate` can each fall back to
        // the deal-level override, so a per-term stamp cannot describe them both — see the grid label selector
        // in `paymentGridSelectors`.
        // The `??` rungs serve the Finance clause alone; the Lease clause requires `ownPricedRateType`.
        const rateTypeValue = usesOwnPricedRate
            ? ownPricedRateType ?? rateTypeByTerm ?? rateType
            : isDealLevelRateTypePath
            ? rateType ?? rateTypeByTerm
            : rateTypeByTerm ?? rateType;

        // The column's own buy rate travels with its own sell rate. Left apart, a term whose rate is below the
        // deal's arrives with no buy rate at all and Payment Services defaults one from the deal — which can
        // land above the sell rate being sent, the pairing `handlePaymentAction` keeps aligned for grid edits.
        const ownPricedBuyRate = usesOwnPricedRate ? ownPricedTerm?.buyRate : undefined;
        // A rate never travels without a buy rate: left apart, Payment Services defaults one from the deal and
        // can land it above the sell rate being sent for this column.
        const columnBuyRate = ownPricedBuyRate ?? (needsCompleteRatePair ? buyRate ?? lastResortBuyRate : undefined);

        const isForcedOwnColumn = isLeaseGridForcedColumnCall && !isSelectedTerm;
        const termOverrideForThisColumn = options.ignoreTermSellRateOverride ? undefined : sellRateByOfferTypeAndTerm;
        const resolvedSellRate =
            options.columnSellRate ??
            (isForcedOwnColumn
                ? termOverrideForThisColumn ?? (usesOwnPricedRate ? ownPricedSellRate : undefined)
                : termOverrideForThisColumn ?? sellRateFallback ?? forcedFallbackSellRate ?? lastResortSellRate);

        const resolvedBuyRate = useBuyRateOverride || isSelectedTerm ? buyRateOverrideByOfferTypeAndTerm ?? buyRate : columnBuyRate;

        const sendsColumnsOwnRate = options.columnSellRate != null || termOverrideForThisColumn != null;
        const ownSellRateUnit = sendsColumnsOwnRate
            ? getColumnOwnRateType(state, offerType, term, options, options.columnSellRate != null)
            : undefined;

        const columnRateUnit = ownSellRateUnit ?? (usesOwnPricedRate ? ownPricedRateType : undefined);
        const dealRateUnit = rateType ?? offerSelectors.getRateTypeByTerm(state, offerType, currentTerm as number);
        const pricedTermRecord = ownPricedTerm ?? offerSelectors.getTermByOfferTypeAndMonths(state, offerType, term);
        const pairedBuyRate = sendsDuplicatedColumnOwnRate(state, offerType, term, options)
            ? options.columnSellRate
            : offerType === LEASE && columnRateUnit != null && dealRateUnit != null && columnRateUnit !== dealRateUnit
            ? (isRateInAnotherUnit(columnRateUnit, buyRateOverrideByOfferTypeAndTerm)
                  ? undefined
                  : buyRateOverrideByOfferTypeAndTerm) ??
              (pricedTermRecord?.rateType === columnRateUnit ? pricedTermRecord?.buyRate : undefined) ??
              resolvedSellRate
            : resolvedBuyRate;

        // Nothing knows this column's unit, and a guess is not a neutral default: `MoneyFactor` against a column
        // quoted in APR is the mismatch this whole block exists to prevent. Observed after a grid opened with Auto
        // Select ON was switched off - the column's term record carries no rate type, the deal-level override is
        // absent, and the payload declared MoneyFactor, so Payment Services priced it as one and the cell flipped
        // from APR to MF (DE762937). Left undefined the key never reaches the wire.
        // A forced Finance column that could resolve no rate takes the guessed unit with it: a unit alone describes
        // nothing. One that DID resolve a rate still needs a unit, or it is the other half of the same rejected pair.
        const declaredRateType =
            ownSellRateUnit ??
            rateTypeValue ??
            (isRedesignLeaseGridCall || (isForcedFinanceGridCall && resolvedSellRate == null)
                ? undefined
                : (DEFAULT_RATE_TYPE_MAPPING as any)[offerType]);

        // A buy rate on its own is the same rejected half pair as a rate with no unit. On a forced column call the
        // sell rate is deliberately withheld unless the column owns one, while a per-term buy-rate override still
        // resolved - so the column went out with a buy rate, no sell rate and a guessed unit, and Payment Services
        // priced it from figures the user never chose.
        // Forced Finance is held to the same rule: it withholds a rate no record can supply, and an ASP-off build
        // seeds an undefined sell rate beside a resolved buy rate, so the half pair is reachable there too.
        const maySendBuyRate =
            pairedBuyRate != null && (resolvedSellRate != null || !(isRedesignLeaseGridCall || isForcedFinanceGridCall));

        const ratePairIsImplausible = isImplausibleMoneyFactor(declaredRateType, resolvedSellRate, pairedBuyRate);

        return {
            ...acc,
            [term]: {
                rateType: declaredRateType,
                ...(ratePairIsImplausible ? undefined : { sellRate: resolvedSellRate }),
                ...(maySendBuyRate && !ratePairIsImplausible && { buyRate: pairedBuyRate })
            }
        };
    }, {});
};

/**
 * A Lease grid row forced onto user-program-quotes sends a rate only for the columns that have one of their own. On that
 * engine the supplied rate IS what prices the payment, and both fallbacks below are substitutions - `sellRate` is the
 * deal screen's override and `forcedFallbackSellRate` the active column's - so a blank column came back priced at the
 * deal's money factor.
 *
 * Narrow on three axes: FORCED calls only, since program-quotes is governed by its pinned program; NON-CURRENT terms
 * only, since the deal's rate IS the current column's own; and NAMED terms only, so deal-screen payloads are untouched.
 */
const singleGridColumnOf = (columnIndices: unknown): number | undefined =>
    Array.isArray(columnIndices) && columnIndices.length === 1 ? (columnIndices[0] as number) : undefined;

const numberOrUndefined = (value: unknown): number | undefined => (typeof value === 'number' ? value : undefined);

const ownQuotedResidual = (state: GenericObject, months: number, columnIndex: number | undefined): ResidualInfo | undefined => {
    if (columnIndex == null) return undefined;
    const rows = offerSelectors.getPaymentGridColumnCells(state, LEASE)[columnIndex] ?? {};
    const cell = Object.values(rows).find((row) => row?.term === months && row?.monthlyPayment != null);
    if (!cell) return undefined;
    return {
        baseResidualPercent: numberOrUndefined(cell.baseResidualPercent) ?? numberOrUndefined(cell.residualPercentage),
        baseResidualAmount: numberOrUndefined(cell.baseResidualDollar),
        programResidual: numberOrUndefined(cell.programResidual),
        programResidualPercent: numberOrUndefined(cell.programResidualPercent) ?? numberOrUndefined(cell.residualPercentage)
    } as ResidualInfo;
};

const applyGridColumnResiduals = (
    state: GenericObject,
    byTerm: Record<number, any>,
    currentTerm: number,
    options: RequestOverrideOptions,
    skipAdjustedResidualValues: boolean
): void => {
    if (!options.forceUseGenericCalcs) return;

    // The same gate every other seam routes through, so the payload builder cannot disagree with the grid, the
    // middleware and the components. A raw toggle read differs on Flex Buy: `isFlexBuyPayment` reads the FINANCE node
    // regardless of the current offer type.
    const isCellSelection = Boolean(options.isGridCellSelection);
    const terms = options.termList?.length ? options.termList : [currentTerm];

    // Every term reached here has an entry: `byTerm` is built by getPaymentGridOverridesByTerm from this same
    // `options.termList` (or `[currentTerm]` when there is none), and its reduce writes one per term
    // unconditionally. Deliberately not guarded - a silent `continue` on a missing entry would drop the
    // residual and hand back the silent no-payment this whole pass exists to stop.
    for (const term of terms) {
        const termOverrides = byTerm[term];
        if (!isCellSelection && !options.columnResidualInfo && termOverrides.baseResidualValue != null) continue;

        const termResidual = options.ignoreTermResidualOverride
            ? ownQuotedResidual(state, term, options.gridColumnIndex) ?? getLenderResidualForTerm(state, LEASE, term)
            : offerSelectors.getResidualInfoOverrideByOfferTypeTerm(state, LEASE, term, true);
        const residual = options.columnResidualInfo ? { ...termResidual, ...options.columnResidualInfo } : termResidual;
        // Nothing on record for this term: leave whatever is there rather than blanking a value the
        // deal-level block supplied, and never substitute another term's residual.
        if (!residual || isLeaseResidualMissing(residual)) continue;

        // The cell-selection branch must WIN over the deal-level block, which wrote the PREVIOUSLY selected term's
        // residual to `byTerm[currentTerm]` - but winning means overriding with a value, never blanking one.
        // `getResidualInfoOverrideByOfferTypeTerm(..., true)` merges term-record defaults over the penciled override
        // and so always returns an object, which makes the `!residual` guard above dead here; and for a term whose
        // record carries no `programResidualPercent` / `adjustedResidualPercent` it yields `undefined` for exactly
        // those fields. `skipUndefined: false` then wrote `programResidualValue: undefined` over the good deal-level
        // value, dropping overrides the user had set on the deal screen for that same term. `isLeaseResidualMissing`
        // cannot catch it - it checks only the four base/adjusted amount fields, not the program ones.
        const fields = termResidualFieldsFrom(residual, skipAdjustedResidualValues);

        // A resolved base value is the tell that this term's residual really is expressed in the unit the
        // fallback resolved it in. Without it the write would flip `baseResidualType` to the fallback's default
        // (PERCENT for lease) while leaving the deal-level block's dollar values in place, so the payload would
        // describe a percent residual carrying dollar amounts.
        if (isCellSelection && fields.baseResidualValue == null) continue;

        writeTermResidual(termOverrides, residual, { skipAdjustedResidualValues, skipUndefined: true });
    }
};

/**
 * The max-residualized-MSRP pair, or neither half.
 *
 * `0` is not a $0 cap — it is "no cap known", which is what a user-program-quotes response reports, since on
 * that engine the caller owns the residual and no lender cap is resolved. The orchestrator forwards it
 * verbatim (`isNumber(0)` is true), so Payment Services residualizes every term against a $0 MSRP and returns
 * an empty quote list (found under US2067816).
 *
 * `maxResidualizedMsrpApplied` still travels on its own, matching US2067816's tests: reviewers flagged the
 * half pair (PR 10767) but changing it would alter behaviour that story pinned, so it is raised there rather
 * than changed here. Resolved in one place rather than in
 * `getMaxResidualizedMsrpOverrideByOfferType`, whose `enableFallback` branch returns `0` deliberately.
 */
const resolveMaxResidualizedMsrpPair = (state: GenericObject, offerType: OfferType): { maxResidualizedMsrp?: number } => {
    const maxResidualizedMsrp = offerSelectors.getMaxResidualizedMsrpOverrideByOfferType(state, offerType);
    if (!shouldSendMaxResidualizedMsrp(state, offerType, maxResidualizedMsrp)) return {};

    return { maxResidualizedMsrp };
};

/**
 * Resolves the residual type a lease/balloon term is priced in.
 *
 * Payment Services rejects a user-program-quotes quote that carries no residual type at all
 * ("residualtype is required for lease/balloon deals"), and an unset override used to be written straight
 * onto the payload, where `JSON.stringify` dropped the key and left the request invalid. So take the
 * explicit type when there is one, and otherwise read the unit off whichever value is actually present —
 * the same rule `hydrateResidualPair` follows when it seeds the fallback snapshot.
 */
export const resolveResidualType = (
    explicitType: ResidualValueType | undefined,
    percentValue: number | undefined,
    amountValue: number | undefined
): ResidualValueType | undefined => {
    if (explicitType != null) return explicitType;
    if (percentValue != null) return RESIDUAL_PERCENT;
    if (amountValue != null) return RESIDUAL_DOLLAR;
    return undefined;
};

/**
 * The unit one residual is expressed in, read only from its own fields.
 *
 * The program residual counts: a grid residual edit writes that pair alone while `F298789_ASP_PG_redesign`
 * is on (`residualHandlers`), so a dollar edit that leaves `baseResidualType` unset would otherwise be read
 * as a percent and priced off the other unit's stale field.
 */
const resolveBaseResidualUnit = (residual?: ResidualInfo): ResidualValueType | undefined =>
    // The base pair is asked first and on its own: a residual holding a base amount alongside a stale program
    // percent is a dollar residual, and reading the two pairs together would call it a percent.
    resolveResidualType(residual?.baseResidualType, residual?.baseResidualPercent, residual?.baseResidualAmount) ??
    resolveResidualType(undefined, residual?.programResidualPercent, residual?.programResidual);

/**
 * The unit this residual both resolves to and actually carries a figure for.
 *
 * A declared type is not enough on its own. `penciledInputsOverrides` writes `baseResidualTypeOverride` as
 * dollar for every lease while `enableLeaseResidualTreatment_F286227` is off, so a percent-only lender
 * program leaves a deal-level residual that says dollar and holds only percents. Reading the type off that
 * and the value off the same object ships the type alone — the second half of DE762668.
 */
export const resolveCompleteResidualUnit = (residual?: ResidualInfo): ResidualValueType | undefined => {
    const unit = resolveBaseResidualUnit(residual);
    if (unit == null) return undefined;

    const hasValueInUnit =
        unit === RESIDUAL_DOLLAR
            ? residual?.baseResidualAmount != null || residual?.programResidual != null
            : residual?.baseResidualPercent != null || residual?.programResidualPercent != null;

    return hasValueInUnit ? unit : undefined;
};

export const getPqLeaseResidualOverride = (state: GenericObject): LeaseResidualOverride | undefined => {
    if (!offerSelectors.getIsProgramQuotes(state, LEASE)) return undefined;

    const months = offerSelectors.getSelectedTermMonths(state, LEASE);
    if (months == null) return undefined;

    const residualInfo = offerSelectors.getResidualInfoOverrideByOfferTypeTerm(state, LEASE, months);
    const unit = resolveCompleteResidualUnit(residualInfo);
    if (!residualInfo || !unit) return undefined;

    const value = unit === RESIDUAL_DOLLAR ? residualInfo.baseResidualAmount : residualInfo.baseResidualPercent;
    if (!value) return undefined;

    const leaseResidualOverride = { months, unit, value };
    const pricedTerm = offerSelectors.getSelectedTermByOfferType(state, LEASE);

    return isPricedWithLeaseResidualOverride(pricedTerm, leaseResidualOverride) ? leaseResidualOverride : undefined;
};

/**
 * Whether a Maximum Residualized MSRP is a real lender cap worth sending. `0` is not a $0 cap - it is "no cap known",
 * and it is what a user-program-quotes response reports. `penciledInputsStateUtils` copies it onto the deal, and the
 * orchestrator forwards it verbatim (`isNumber(0)` is true), so Payment Services residualizes every term against a
 * $0 MSRP and returns an empty quote list. Behind the redesign gate, so toggle-off payloads stay byte-identical.
 */
const pickBaseResidualSource = (candidates: (ResidualInfo | undefined)[]): ResidualInfo | undefined =>
    candidates.find((candidate) => candidate?.baseResidualType != null && resolveCompleteResidualUnit(candidate) != null) ??
    candidates.find((candidate) => resolveCompleteResidualUnit(candidate) != null);

// A resolved base value is the tell that this term's residual really is expressed in the unit the fallback resolved
// it in. Without it the write flips `baseResidualType` while leaving the deal-level dollar values in place.
const getLenderResidualForTerm = (state: GenericObject, offerType: OfferType, months: number): ResidualInfo => {
    const term = offerSelectors.getTermByOfferTypeAndMonths(state, offerType, months);

    // A balloon residual is the lump balloon payment, so it is dollar by nature — the selected-term block
    // hardcodes RESIDUAL_DOLLAR, and `getResidualInfoOverrideByOfferTypeTerm` defaults non-lease to dollar.
    // Balloon terms do carry `baseResidualPercent`, so deriving the unit here would price a balloon column
    // as a percent while the selected term in the same payload is priced in dollars.
    if (offerType !== LEASE) {
        return {
            baseResidualType: RESIDUAL_DOLLAR,
            baseResidualAmount: term?.baseResidualDollar,
            programResidual: term?.programResidual
        } as ResidualInfo;
    }

    // No declared type: the unit is derived from whichever figure this term actually carries, so the value
    // travels with it. A term absent from `offer.terms` yields nothing here on purpose — naming the offer
    // type's default would emit a unit with no value, which Payment Services rejects just as it rejects a
    // value with no unit.
    return {
        baseResidualPercent: term?.baseResidualPercent,
        baseResidualAmount: term?.baseResidualDollar,
        programResidual: term?.programResidual,
        programResidualPercent: term?.programResidualPercent
    } as ResidualInfo;
};

/**
 * The lender residual a grid column may be priced with, or `undefined` when it has none it can claim.
 *
 * `offer.terms` is merge-only, so a column carries whatever an earlier program-quotes build left on it. For a
 * column THIS build quoted that figure is the one that priced it and belongs in the payload. For a column the
 * build never quoted it is stale: sending it quoted the column off a residual the user never chose and then
 * painted that figure into the cell, since the Residual row reads what came back (DE762937).
 *
 * `isMasked` is the tell that columns are gated by the build at all - it is true only for a Lease redesign grid.
 * Everywhere else this returns the lender residual exactly as before, so the pre-redesign grid and the Alt Deal
 * Structure lease grid keep the DE762668 fallback they depend on.
 */
const getClaimableLenderResidualForTerm = (
    state: GenericObject,
    offerType: OfferType,
    months: number
): ResidualInfo | undefined => {
    const lenderResidual = getLenderResidualForTerm(state, offerType, months);
    const { isMasked, quotedTerms } = offerSelectors.getPaymentGridColumnView(state, offerType);

    if (!isMasked) return lenderResidual;

    return quotedTerms.has(months) ? lenderResidual : undefined;
};

const canClaimOwnPricedRateForTerm = (state: GenericObject, offerType: OfferType, months: number): boolean => {
    const { isMasked, quotedTerms } = offerSelectors.getPaymentGridColumnView(state, offerType);
    return isMasked ? quotedTerms.has(months) : true;
};

/**
 * Gives a Lease term being priced on its own the residual for **its own term**. Every residual write above this is keyed
 * to `byTerm[currentTerm]` and read from the deal-level lease inputs, which describe whichever term the main screen was
 * last priced at - while Payment Services reads the residual per term. Two modes:
 *
 * * **A grid column priced by itself** - applied additively, so a term already written above is left alone.
 * * **A cell selection onto a user-program-quotes column** - the per-term value has to WIN, or the main screen lands on
 *   a different payment from the cell that was clicked. Identified by `options.isGridCellSelection`.
 *
 * Both read the per-term residual the app already holds, so nothing is invented: a term with nothing on record still
 * sends nothing. Both require a forced user-program-quotes call, which only the grid issues.
 */
const applyPerTermResidualOverrides = (
    state: GenericObject,
    byTerm: Record<number, any>,
    offerType: OfferType,
    currentTerm: number,
    skipAdjustedResidualValues: boolean,
    ignoreTermResidualOverride = false
) => {
    /** Read off the action: a cell selection always names its term on the wire, so a term-list test cannot detect one. */
    const isPerTermResidualEnabled = !!state.featureToggles?.F298789_ASP_PG_redesign;

    // Every term reached here has an entry: `byTerm` is built by getPaymentGridOverridesByTerm from this same term list.
    // Deliberately unguarded - a silent `continue` would drop the residual and hand back the silent no-payment this pass
    // exists to stop.
    const needsLenderResidual = byTerm[currentTerm] == null;

    Object.keys(byTerm).forEach((term) => {
        const months = Number(term);
        if (months === currentTerm) return;
        // Nothing on record for this term: leave what is there rather than blanking a deal-level value, and never
        // substitute another term's residual.
        if (byTerm[months] == null) return;

        const residualForTerm =
            isPerTermResidualEnabled && !ignoreTermResidualOverride
                ? offerSelectors.getResidualInfoOverrideByOfferTypeTerm(state, offerType, months)
                : undefined;

        // The cell-selection branch must WIN over the deal-level block, but winning means overriding with a value, never
        // blanking one: `getResidualInfoOverrideByOfferTypeTerm(..., true)` always returns an object and yields
        // `undefined` for program fields a term's record lacks, which `isLeaseResidualMissing` cannot catch.
        const baseResidualForTerm = pickBaseResidualSource(
            needsLenderResidual ? [residualForTerm, getClaimableLenderResidualForTerm(state, offerType, months)] : [residualForTerm]
        );
        const residualTypeForTerm = resolveCompleteResidualUnit(baseResidualForTerm);

        const isDollar = residualTypeForTerm === RESIDUAL_DOLLAR;
        const baseResidualValueForTerm = isDollar
            ? baseResidualForTerm?.baseResidualAmount
            : baseResidualForTerm?.baseResidualPercent;
        const programResidualValueForTerm = isDollar
            ? baseResidualForTerm?.programResidual
            : baseResidualForTerm?.programResidualPercent;

        // Never on its own: a unit with no figure is the same rejected pair as a figure with no unit.
        if (residualTypeForTerm != null) {
            byTerm[months].baseResidualType = residualTypeForTerm;
        }
        if (baseResidualValueForTerm != null) {
            byTerm[months].baseResidualValue = baseResidualValueForTerm;
        }
        if (programResidualValueForTerm != null) {
            byTerm[months].programResidualValue = programResidualValueForTerm;
        }

        if (!skipAdjustedResidualValues && residualForTerm) {
            const adjustedTypeForTerm = residualForTerm.adjustedResidualType;
            const adjustedValueForTerm =
                adjustedTypeForTerm === RESIDUAL_DOLLAR
                    ? residualForTerm.adjustedResidualAmount
                    : residualForTerm.adjustedResidualPercent;

            if (adjustedTypeForTerm != null) {
                byTerm[months].adjustedResidualType = adjustedTypeForTerm;
            }
            if (adjustedValueForTerm != null) {
                byTerm[months].adjustedResidualValue = adjustedValueForTerm;
            }
        }
    });
};

const getContractingLenderCode = (state: GenericObject, offerType: OfferType): string | undefined =>
    state.featureToggles?.enablePorscheProRata || state.featureToggles?.F294383_enableReserveSplit
        ? offerSelectors.getCreditDecisionLenderByOfferType(state, offerType)?.id
        : undefined;

export function createPaymentOverridesForGenericCalcs(
    state: GenericObject,
    offerType: OfferType,
    options: RequestOverrideOptions = {}
): GenericObject {
    const retailPrice = offerSelectors.getRetailPriceOverrideByOfferType(state, offerType);
    const securityDeposit = offerSelectors.getSecurityDepositOverrideByOfferType(state, offerType);
    const acqFee = state.featureToggles?.enableLenderFeeEdits
        ? null
        : offerSelectors.getAcqFeeOverrideByOfferType(state, offerType);
    const acqFeeUpFront = state.featureToggles?.enableLenderFeeEdits
        ? null
        : offerSelectors.getAcqFeeUpFrontOverrideByOfferType(state, offerType);
    const mileageCharge = offerSelectors.getMileageChargeOverrideByOfferType(state, offerType);
    const incentivesAmount = offerSelectors.getIncentivesTotalOverrideByOfferType(state, offerType);
    const manualIncentives = offerSelectors.getManualIncentives(state, offerType);
    const initialPaymentDiscount = offerSelectors.getOfferInitialPaymentDiscount(state);

    const isReserveSplitOverrideEnabled = state.featureToggles?.F294383_enableReserveSplit;
    const lenderCode = getContractingLenderCode(state, offerType) ?? options.lockedLenderCode;

    const overrides: GenericObject = {
        ...(acqFee != null ? { acqFee } : undefined),
        ...(acqFeeUpFront != null ? { acqFeeUpFront } : undefined),
        ...(securityDeposit != null ? { securityDeposit } : undefined),
        ...(retailPrice != null ? { retailPrice } : undefined),
        ...(mileageCharge != null ? { mileageCharge } : undefined),
        ...(incentivesAmount != null ? { incentivesAmount } : undefined)
    };

    const currentTerm = offerSelectors.getSelectedTermMonths(state, offerType) as number;

    let byTerm: Record<number, any> = getPaymentGridOverridesByTerm(state, offerType, options);

    /**
     * A falling-back Finance column keeps the program it was quoting against - Payment Services reads this slot on
     * user-program-quotes too. Only where the grid is pricing against programs at all: where it is not, the per-term
     * cache outlives the grid, so a forced row would be re-pinned to a program nothing on the deal names any more.
     *
     * `isValidProgram` alone, unlike the program-quotes builder, which also sends a partial program when Sales View
     * overrides are explicitly off: a half-formed program is worse than none on an engine that prices from what it is
     * given.
     */
    const carriesProgramFigures =
        isFinanceGridForcedColumnCall(state, offerType, options) && !offerSelectors.getGridIsUserProgramQuotes(state, offerType);

    if (carriesProgramFigures) {
        const shouldSkipProgram =
            options.skipProgramOverride || (!options.isExplicitProgramLock && getIsShopperUpdateFailed(state));
        Object.keys(byTerm).forEach((term: any) => {
            const months = Number(term);
            // A term list carrying a null leaves a non-numeric key, and `getPaymentProgramInfoByTerm` reads a falsy
            // months as the deal's own term - which would stamp that phantom column with the deal's program.
            if (!Number.isFinite(months)) return;

            const program = shouldSkipProgram ? null : resolveProgramForRequest(state, offerType, months, options);
            if (isValidProgram(program)) byTerm[term].program = program;
        });
    }

    if (offerType === LEASE) {
        if (options?.isRollToCall) {
            const termMonths = options.termMonths || currentTerm;
            const residualType = RESIDUAL_DOLLAR;
            const baseResidualValue = offerSelectors.getBaseResidualDollar(state);
            const programResidualValue = offerSelectors.getProgramResidual(state);
            const adjustedResidualValue = offerSelectors.getAdjustedResidualValue(state);

            byTerm = {
                ...byTerm,
                ...{
                    [termMonths]: {
                        ...byTerm[termMonths],
                        ...{
                            baseResidualType: residualType,
                            baseResidualValue,
                            programResidualValue,
                            adjustedResidualType: residualType,
                            adjustedResidualValue
                        }
                    }
                }
            };
        } else {
            const leaseInputs = offerSelectors.getOfferDetails(state, LEASE);

            // The deal-level overrides are the selected term's residual, but a residual entered while the deal
            // was on program quotes lives only in that term's own `overridesByTerm` entry, leaving the
            // deal-level slot empty. Writing the type through unchecked sent Payment Services a residual value
            // with no type, which it rejects outright (DE762668) — so resolve type and value together, from
            // whichever of the two actually holds a residual.
            const dealLevelResidual = {
                baseResidualType: leaseInputs.baseResidualTypeOverride,
                baseResidualPercent: leaseInputs.baseResidualPercentOverride,
                baseResidualAmount: leaseInputs.baseResidualAmountOverride,
                programResidual: leaseInputs.programResidualOverride,
                programResidualPercent: leaseInputs.programResidualPercentOverride
            } as ResidualInfo;
            const residualForSelectedTerm = offerSelectors.getResidualInfoOverrideByOfferTypeTerm(state, LEASE, currentTerm);

            // A deal that owns no residual at all falls back to the lender's figures for this term — every
            // reopened deal is in that state, since `offerPayloadUtil` persists the adjusted residual
            // overrides but none of the base ones. Payment Services requires both halves under
            // user-program-quotes and derives neither, so type and values come from one source together.
            const ownedBaseResidual = options.ignoreTermResidualOverride
                ? undefined
                : pickBaseResidualSource([dealLevelResidual, residualForSelectedTerm]);
            const baseResidualSource =
                ownedBaseResidual ??
                pickBaseResidualSource([
                    ...(options.ignoreTermResidualOverride ? [ownQuotedResidual(state, currentTerm, options.gridColumnIndex)] : []),
                    getLenderResidualForTerm(state, LEASE, currentTerm)
                ]);

            const baseResidualType = resolveCompleteResidualUnit(baseResidualSource);
            const isDollarBaseResidual = baseResidualType === RESIDUAL_DOLLAR;
            const baseResidualValue = isDollarBaseResidual
                ? baseResidualSource?.baseResidualAmount
                : baseResidualSource?.baseResidualPercent;
            const programResidualValue = isDollarBaseResidual
                ? baseResidualSource?.programResidual
                : baseResidualSource?.programResidualPercent;

            // Read from the deal-level overrides alone, the same source as the values below: the lambda prefers
            // the adjusted type over the base one, so a type borrowed from elsewhere would override a sound
            // base type with a unit these values are not in.
            // Withheld entirely once the base residual came from the lender, because then the deal owns no
            // base residual — and `offerPayloadUtil` persists the adjusted overrides while persisting none of
            // the base ones, so on a reopened deal the surviving adjusted pair is stale. Sending it would win
            // over the base residual just resolved and price the term off a figure the deal no longer holds.
            const adjustedResidualType =
                ownedBaseResidual == null
                    ? undefined
                    : resolveResidualType(
                          leaseInputs.adjustedResidualTypeOverride,
                          leaseInputs.adjustedResidualPercentOverride,
                          leaseInputs.adjustedResidualAmountOverride
                      );
            const excessMileRate = offerSelectors.getExcessMileRate(state);
            const thresholdMiles = offerSelectors.getThresholdMiles(state);
            const inceptionRate = offerSelectors.getInceptionRate(state);
            const retroMiles = offerSelectors.getRetroMiles(state);
            const standardAllowedMiles = offerSelectors.getStandardAllowedMiles(state);
            const retroMilesThreshold = offerSelectors.getRetroMilesThreshold(state);

            const skipAdjustedResidualValues = skipAdjustedResidualValuesFor(state, options);

            if (byTerm[currentTerm]) {
                // The type is only named alongside a figure it belongs to: `resolveCompleteResidualUnit`
                // returns a unit only when this source holds a value in it, so the pair travels together or
                // not at all — a half pair is what Payment Services rejects.
                if (baseResidualType != null) {
                    byTerm[currentTerm].baseResidualType = baseResidualType;
                    byTerm[currentTerm].baseResidualValue = baseResidualValue;
                    byTerm[currentTerm].programResidualValue = programResidualValue;
                }

                // Preserves pre-F286227 behavior where adjustedResidual values are supplied in the payment
                // call. Type and value are written together for the same reason the base pair is: the lambda
                // reads `adjustedResidualValue || baseResidualValue`, so an adjusted value with no adjusted
                // type would be paired with the base *type* and priced in a unit it is not expressed in.
                if (!skipAdjustedResidualValues && adjustedResidualType != null) {
                    byTerm[currentTerm].adjustedResidualType = adjustedResidualType;
                    byTerm[currentTerm].adjustedResidualValue =
                        adjustedResidualType === RESIDUAL_DOLLAR
                            ? leaseInputs.adjustedResidualAmountOverride
                            : leaseInputs.adjustedResidualPercentOverride;
                }

                byTerm[currentTerm].upfrontMileageCharge = excessMileRate;
                byTerm[currentTerm].initialMilesThreshold = thresholdMiles;
                byTerm[currentTerm].initialMilesCharge = inceptionRate;
                byTerm[currentTerm].retroMiles = retroMiles;
                byTerm[currentTerm].standardMiles = standardAllowedMiles;
                byTerm[currentTerm].retroMilesThreshold = retroMilesThreshold;

                Object.assign(byTerm[currentTerm], resolveMaxResidualizedMsrpPair(state, offerType));
                const maxResidualizedMsrpAppliedOverride = offerSelectors.getMaxResidualizedMsrpAppliedOverrideByOfferType(
                    state,
                    offerType
                );
                if (maxResidualizedMsrpAppliedOverride != null) {
                    byTerm[currentTerm].maxResidualizedMsrpApplied = maxResidualizedMsrpAppliedOverride;
                }
            }

            // Mileage and max-residualized-MSRP describe the lease, not one term of it, but they were only
            // ever written onto the selected term's entry. A request that excludes the selected term — a
            // residual or sell rate edit on a non-active grid column narrows `termList` to that column — then
            // carried no mileage terms at all, so Payment Services quoted that column off its own defaults
            // instead of the deal's allowance (DE762937 issue 2).
            // Spread to EVERY requested column, not only to the columns of a request that happens to exclude the
            // selected term: gating on that made the same cell quote two different payments in one session — a
            // full-grid build left the other columns on Payment Services' own mileage defaults, then a residual
            // edit narrowed the request, the spread fired, and the cell came back at a different number.
            // Still behind the redesign toggle, so a toggle-off multi-term lease request keeps today's shape.
            // On the columns this adds, each key is written only when it has a value, so they gain no explicit
            // `undefined` properties — correctness must not rest on `JSON.stringify` dropping them. The
            // selected term's own block above keeps assigning unconditionally: that shape predates this change
            // and is left alone.
            const maxResidualizedMsrpAppliedForSpread = offerSelectors.getMaxResidualizedMsrpAppliedOverrideByOfferType(
                state,
                offerType
            );
            const dealLeaseValues: GenericObject = {
                ...(excessMileRate != null && { upfrontMileageCharge: excessMileRate }),
                ...(thresholdMiles != null && { initialMilesThreshold: thresholdMiles }),
                ...(inceptionRate != null && { initialMilesCharge: inceptionRate }),
                ...(retroMiles != null && { retroMiles }),
                ...(standardAllowedMiles != null && { standardMiles: standardAllowedMiles }),
                ...(retroMilesThreshold != null && { retroMilesThreshold }),
                ...resolveMaxResidualizedMsrpPair(state, offerType),
                // The applied flag travels with the cap here too. Sending the cap alone on these columns is the
                // mirror of the half pair: the same deal would residualize a column differently depending on
                // whether the request happened to include the selected term.
                ...(maxResidualizedMsrpAppliedForSpread != null && {
                    maxResidualizedMsrpApplied: maxResidualizedMsrpAppliedForSpread
                })
            };

            // Raw keys, not `Object.keys(...).map(Number)`: a term list built without a selected term can
            // leave a non-numeric key, which `Number` turns into NaN and indexes back to nothing.
            if (state.featureToggles?.F298789_ASP_PG_redesign) {
                Object.keys(byTerm).forEach((term: any) => {
                    Object.assign(byTerm[term], dealLeaseValues);
                });
            }

            // Called unconditionally: DE762668 made this helper the ONLY place that can name a residual *type* for a
            // request whose term list excludes the deal's selected term, and Payment Services rejects a
            // user-program-quotes quote carrying none. The per-term override carry is still toggled inside the helper.
            applyPerTermResidualOverrides(
                state,
                byTerm,
                LEASE,
                currentTerm,
                skipAdjustedResidualValues,
                options.ignoreTermSellRateOverride || options.ignoreTermResidualOverride
            );
            // Runs after the per-term carrier, and only for a forced grid call. On a cell selection it must overwrite
            // `currentTerm`, which that pass deliberately skips.
            applyGridColumnResiduals(state, byTerm, currentTerm, options, skipAdjustedResidualValues);
        }
    }

    if (offerType === BALLOON && byTerm[currentTerm]) {
        const balloonOffer = offerSelectors.getOfferDetails(state, BALLOON);
        const baseResidualType = RESIDUAL_DOLLAR;
        const adjustedResidualType = RESIDUAL_DOLLAR;

        byTerm[currentTerm].balloonTerm = balloonOffer.balloonTerm;
        byTerm[currentTerm].balloonType = formatBalloonType(balloonOffer.balloonTypeOverride ?? balloonOffer.balloonType);
        byTerm[currentTerm].baseResidualType = baseResidualType;
        byTerm[currentTerm].baseResidualValue =
            balloonOffer.balloonPaymentAmountOverride ?? balloonOffer.baseResidualAmountOverride;
        byTerm[currentTerm].programResidualValue =
            balloonOffer.balloonPaymentAmountOverride ?? balloonOffer.programResidualOverride;
        byTerm[currentTerm].adjustedResidualType = adjustedResidualType;
        byTerm[currentTerm].adjustedResidualValue =
            balloonOffer.balloonPaymentAmountOverride ?? balloonOffer.adjustedResidualAmountOverride;
    }

    if (offerType === BALLOON) {
        // Balloon always supplies its adjusted residual above, so the per-term carry-forward does too.
        applyPerTermResidualOverrides(state, byTerm, BALLOON, currentTerm, false);
    }
    const isFlexBuyPayment = offerSelectors.isFlexBuyPayment(state);

    if (offerType === FINANCE && isFlexBuyPayment && byTerm[currentTerm]) {
        byTerm[currentTerm].initialPaymentDiscount = initialPaymentDiscount;
    }

    if (lenderCode != null && byTerm[currentTerm]) {
        byTerm[currentTerm].lenderCode = lenderCode;
    }

    const reserveSplitOverride = isReserveSplitOverrideEnabled
        ? offerSelectors.getReserveSplitOverride(state, offerType)
        : undefined;

    if (reserveSplitOverride != null && byTerm[currentTerm]) {
        byTerm[currentTerm].reserveSplit = reserveSplitOverride;
    }

    overrides.byTerm = byTerm;

    // Generic-calcs path: carries the dealer's own rebate amount, same rationale as
    // getManualIncentivesRequestNode above. Gate on the applied total rather than the manual-incentive bag:
    // a payment-grid checkbox toggle can delete the bag while a rebate is still applied, and Payment Services
    // reads the amount from overrides.manualIncentives.rebates on this path, so gating on the bag alone drops it.
    //
    // Only reach for the applied total when this offer type has penciled inputs, AND this isn't a genuine grid
    // column deriving its own incentive figure from the engine — that only happens on the redesign path
    // (isFinanceGridForcedColumnCall, immediately below, requires isAspPaymentGridRedesignActive for the same
    // reason). The legacy grid has no such mechanism, so isGridColumnCall is gated on the redesign being active
    // too: without that, any named-term call while the legacy grid happens to be open — a program change, a
    // structural recalc, an in-grid rate edit — dropped a still-applied rebate with nothing to replace it.
    //
    // A call naming no terms at all (a plain deal-screen recalc, or the degenerate empty-termList grid row
    // where every term was filtered out as unpriceable) reaches here unconditionally regardless of grid state.
    //
    // options.isDealScreenSingleTermOverride: even on a genuine redesign row, a single named term while the
    // grid is open can still be a plain deal-screen Rate/residual edit rather than a grid engine pricing this
    // column. Keyed on origin, stamped in paymentGridMiddleware.ts.
    //
    // Also reached, independently, when carriesProgramFigures: a forced Finance grid column call carries the
    // applied incentives whether or not the deal holds a manual one, because that engine derives none of its own.
    const namesTerms = (options.termList?.length ?? 0) > 0;
    const isGridColumnCall =
        offerSelectors.isAspPaymentGridRedesignActive(state, offerType) &&
        namesTerms &&
        Boolean(offerSelectors.getPaymentGridToggle(state, offerType)) &&
        !options.isDealScreenSingleTermOverride;
    const offerTypeHasInputs = !isGridColumnCall && hasOfferTypeInputs(state, offerType);
    const carriesLastProgramQuotesTotal =
        isGridIncentiveManagementEnabled(state, offerType) &&
        !options.isFlexBuyGridCall &&
        !leavesDealerStandardsOut(state, offerType, options.isManagerView) &&
        hasOfferTypeInputs(state, offerType);
    if (manualIncentives || offerTypeHasInputs || carriesProgramFigures || carriesLastProgramQuotesTotal) {
        const { rebates, dealerCash } = carriesLastProgramQuotesTotal
            ? offerSelectors.getCarriedUpqRebateTotals(state, offerType)
            : {
                  rebates: getGenericCalcsRebates(state, offerType, options.isManagerView),
                  dealerCash: offerSelectors.getAppliedIncentivesTotalForDealerCash(state, offerType)
              };
        // `manualIncentives` here is `manualIncentives.incentives` — the row array, not the node — and this
        // is bare truthiness over three states: populated, `[]` (present but empty, what a grid checkbox
        // uncheck or an emptied frozen set leaves), and `undefined` (the node is absent, or carries no
        // `incentives` key). `[]` is truthy, so an empty rows array DOES emit an explicit `{rebates: 0}`; it
        // does not behave like `undefined`. That is the point: an explicit zero states "no rebate", where an
        // absent field leaves Payment Services to apply its own for a fallback column. Load-bearing — don't
        // tighten it to a shape check.
        //
        // getManualIncentivesRequestNode above has no equivalent suppression (it sets `rebates`
        // unconditionally once inside the generic branch) — pre-existing, not changed by this fix. This
        // guard only decides whether an all-zero field is emitted when the rows array is ABSENT.
        if (
            manualIncentives ||
            rebates ||
            dealerCash ||
            offerSelectors.getAppliedIncentivesTotalForNonDealerCash(state, offerType)
        ) {
            overrides.manualIncentives = { rebates, dealerCash } as ManualIncentivesRequest;
        }
    }

    return overrides;
}

export const getVdpUrl = (state: GenericObject): string | undefined => {
    return state.vehicle?.vehicleUrl || state.vdpSource?.vdpUrl;
};

export const getDealerAddress = (
    state: GenericObject,
    dealer: MongoDealer,
    passCustomerVersionedAddressToPS = false
): PaymentDealerAddress | undefined => {
    const paymentAddress = state.payment?.dealerTaxAddress;
    const dealerAddress = dealer?.address;

    if (passCustomerVersionedAddressToPS && !paymentAddress) {
        if (!dealerAddress) {
            return undefined;
        }

        const { zip, street, city, state } = dealerAddress;

        return {
            zip,
            street,
            city,
            state
        };
    }

    if (!paymentAddress) {
        return undefined;
    }

    const { postalCode, addressLine1, city, stateProvince } = paymentAddress;

    return {
        zip: postalCode,
        street: addressLine1,
        city: city,
        state: stateProvince
    };
};

export const getFeesAndTaxesSettings = (state: GenericObject): PaymentPayloadCalculationSettings => {
    const payment = state.payment;

    return {
        includeDefaultDealerFees: payment.useDealerDefaultDealerFees,
        includeDefaultGovFees: payment.useDealerDefaultGovFees,
        includeDefaultLenderFees: payment.useDealerDefaultLenderFees,
        includeDefaultDealerBasedTaxes: payment.useDealerDefaultDealerBasedTaxes,
        includeDefaultTaxes: payment.useDealerDefaultTaxes,
        includeCalculatedGovFees: payment.useCalculatedGovFees
    };
};

export const LOCKED_PROGRAM_META = 'lockedProgram';

export const FROZEN_LENDER_FEE_OVERRIDES_META = 'frozenLenderFeeOverrides';

const EXPLICIT_LOCK_EXEMPT_ACTIONS: ReadonlySet<string> = new Set([
    APPLIED_PROGRAM,
    SWITCH_VEHICLE,
    APPLY_RECOMMENDED_VEHICLE_PAYMENT_DETAILS
]);

export const getActionMetaValue = (
    action: PaymentReduxAction,
    property: string,
    fallBack?: boolean | number | string | GenericObject
) => {
    return (action?.meta as any)?.[property] ?? fallBack;
};

const isZipChangingShopperApply = (action: PaymentReduxAction): boolean =>
    action?.type === UPDATED_SHOPPER_CONTACT && getActionMetaValue(action, 'shopperZipChanged') === true;

export const setActionMetaValue = (
    action: PaymentReduxAction,
    value: undefined | null | boolean | number | string | GenericObject,
    property?: string
): void => {
    action.meta = {
        ...action.meta,
        ...(property ? { [property]: value } : typeof value === 'object' && value)
    };
};

export const isOutsideLienHolderData = (outsideLienholder: OutsideLienHolder): boolean => {
    if (!outsideLienholder || Object.keys(outsideLienholder).length === 0) {
        return false;
    }

    const hasLienHolder = (outsideLienholder?.lienHolder ?? '')?.trim() !== '';
    const hasLienHolderAddress = Object.values(outsideLienholder?.lienHolderAddress ?? {}).some((val) => val?.trim() !== '');
    const hasLienHolderPhoneNumber = (outsideLienholder?.lienHolderPhoneNumber ?? '')?.trim() !== '';

    return hasLienHolder || hasLienHolderAddress || hasLienHolderPhoneNumber;
};

const resolveCustomRateGroup = ({
    state,
    paymentOfferType,
    enableCustomRateCard,
    enableDealerRateCardSupport,
    isManagerView,
    isSalesView,
    shouldOmitCustomRateGroup
}: {
    state: any;
    paymentOfferType: OfferType;
    enableCustomRateCard: boolean;
    enableDealerRateCardSupport: boolean;
    isManagerView: boolean;
    isSalesView: boolean;
    shouldOmitCustomRateGroup: boolean;
}): string | undefined => {
    if (shouldOmitCustomRateGroup) {
        return undefined;
    }
    if (!enableCustomRateCard) {
        return isManagerView && paymentOfferType === FINANCE ? 'Instore' : undefined;
    }
    if (isManagerView || isSalesView) {
        return 'Instore';
    }
    const isUserProgramQuotes = offerSelectors.getIsUserProgramQuotes(state, paymentOfferType);
    if (!isUserProgramQuotes) {
        const customRateGroupFallback = enableDealerRateCardSupport ? undefined : 'Online';
        return offerSelectors.getCustomRateGroup(state) || customRateGroupFallback;
    }
    return undefined;
};

const isRollToTermChange = (action: PaymentReduxAction): boolean =>
    (action?.payload as GenericObject | undefined)?.options?.term?.adjusted === true;

const isProgramCatalogAction = (actionForPayment: string, rollToCatalogEnabled: boolean): boolean =>
    actionForPayment === APPLIED_PROGRAM ||
    actionForPayment === UPDATED_OFFER_TYPE ||
    actionForPayment === SELECTED_TERM ||
    actionForPayment === SELECTED_CREDIT_TIER ||
    (rollToCatalogEnabled && actionForPayment === APPLY_ROLL_TO_OPTIONS) ||
    actionForPayment === SWITCH_VEHICLE ||
    actionForPayment === FETCH_QUESTIONS_OR_PAYMENT;

export const GRID_ASK_TURNED_ON_INCENTIVES_META = 'gridAskTurnedOnIncentives';

/**
 * Telling Payment Services to pick the standard rebates while telling it not to report them gets back a
 * priced rebate no field in the response names (DE768653). An explicit meta value still wins.
 */
export const isGridRowAskingForIncentives = (
    state: any,
    action: PaymentReduxAction,
    offerType: OfferType,
    useGenericCalcs: boolean
): boolean =>
    isRegularGridRowAction(action) &&
    isGridIncentiveManagementEnabled(state, offerType) &&
    !useGenericCalcs &&
    typeof getActionMetaValue(action, 'includeIncentives') !== 'boolean';

export const resolveIncludeIncentives = (
    action: PaymentReduxAction,
    actionForPayment: string,
    programIncentivesRestorationEnabled: boolean,
    serviceChoosesStandards = false,
    rollToCatalogEnabled = false
): boolean | undefined => {
    const explicit = getActionMetaValue(action, 'includeIncentives');
    if (typeof explicit === 'boolean') {
        return explicit;
    }

    return (
        !programIncentivesRestorationEnabled ||
        isProgramCatalogAction(actionForPayment, rollToCatalogEnabled) ||
        serviceChoosesStandards
    );
};

/**
 * `enableEquityAllocationOrder` decides *where* the lease target-cash flag lives in the request: top-level
 * `isTargetCashAmountPaid` (createPaymentPayload) instead of `payment.lease.isTargetOutOfPocket` (setLeaseNode).
 * Manager View only, matching the `equityAllocationMethod` gate. Both call sites must resolve this the same
 * way, otherwise a payload could end up with both fields or neither, so keep this the single read.
 */
const getIsEquityAllocationOrderEnabled = (state: GenericObject, isManagerView?: boolean): boolean =>
    Boolean(isManagerView && state.featureToggles?.enableEquityAllocationOrder);
const PROGRAM_CONTEXT_CHANGING_ACTIONS: ReadonlySet<string> = new Set([
    SELECTED_TERM,
    UPDATED_OFFER_TYPE,
    UPDATED_PAYMENT_FREQUENCY,
    UPDATED_SHOPPER_ZIP
]);

const isProgramContextChange = (action?: AnyFSA): boolean =>
    PROGRAM_CONTEXT_CHANGING_ACTIONS.has(action?.type ?? '') ||
    PROGRAM_CONTEXT_CHANGING_ACTIONS.has((action?.meta?.originalAction as AnyFSA | undefined)?.type ?? '');

const isColumnScopedCall = (action?: AnyFSA): boolean => action?.meta?.columnScopedForDuplicateTerm === true;

const isGridCellCall = (action?: AnyFSA): boolean => {
    const paymentGrid = action?.meta?.paymentGrid as { gridAction?: boolean; isMainPayment?: boolean } | undefined;
    if (!paymentGrid?.gridAction) return false;
    return paymentGrid.isMainPayment !== true && !isColumnScopedCall(action);
};

const GRID_DISCOVERY_ACTIONS: ReadonlySet<string> = new Set([
    LOAD_PAYMENT_GRID_LENDER_PROGRAMS,
    LOAD_PAYMENT_GRID_TERM_LENDER_PROGRAM
]);

const isGridDiscoveryCall = (action?: AnyFSA): boolean =>
    GRID_DISCOVERY_ACTIONS.has(action?.type ?? '') ||
    GRID_DISCOVERY_ACTIONS.has((action?.meta?.originalAction as AnyFSA | undefined)?.type ?? '');

const isGridRequestOnOtherProgram = (state: GenericObject, action: PaymentReduxAction, offerType: OfferType): boolean => {
    if (!getActionMetaValue(action, 'paymentGrid')?.gridAction) return false;
    const termList: number[] | undefined = getActionMetaValue(action, PAYMENT_REQUEST_OVERRIDE_NODE)?.[offerType]?.termList;
    if (!termList?.length) return false;
    const selectedProgram = offerSelectors.getGridColumnProgramInfo(
        state,
        offerType,
        offerSelectors.getSelectedTermMonths(state, offerType)
    );
    if (!isValidProgram(selectedProgram)) return false;
    return termList.every((term) => {
        const columnProgram = offerSelectors.getGridColumnProgramInfo(state, offerType, term);
        return isValidProgram(columnProgram) && !isSameLenderProgram(columnProgram, selectedProgram);
    });
};

// A program switch hands the standards decision back to the program. A context change only does so
// while the user has not recorded one of their own, so an unchecked standard survives a term change.
export const resolveIncludeStandardIncentives = (state: GenericObject, action: PaymentReduxAction, offerType: OfferType): boolean =>
    getActionMetaValue(action, 'includeStandardIncentives', false) ||
    ((isProgramContextChange(action) || isGridCellCall(action)) &&
        !offerSelectors.hasRecordedNonConditionalSelection(state, offerType) &&
        (isGridCellCall(action) || !offerSelectors.areProgramStandardsFetched(state, offerType))) ||
    (isConditionalRebateKeepGridOpenEnabled(state) &&
        (isGridDiscoveryCall(action) || isGridRequestOnOtherProgram(state, action, offerType)));

// DE773489: a deal whose customer has no address still arrives with taxes calculated from the dealer's
// location, and keying on the shopper zip alone zeroed them on the first Payment Grid open. Gated on
// the deal already having taxes: preserve what Payment Services supplied, never ask it to invent one.
const resolveIncludeTaxes = (
    state: GenericObject,
    shopperZip: string | undefined,
    includeTaxesSetting: boolean,
    isManagerView: boolean | undefined,
    offerType: OfferType
): boolean => {
    const isEnabled = Boolean(state.featureToggles?.DE773489_Enable_Taxes_Without_Shopper_Zip);

    if (isEnabled && isManagerView) {
        const dealAlreadyHasTaxes = offerSelectors.getTotalTax(state, offerType) > 0;
        return Boolean((shopperZip || dealAlreadyHasTaxes) && includeTaxesSetting);
    }

    return Boolean(shopperZip && includeTaxesSetting);
};

export const createPaymentPayload = (
    state: any,
    hostSelectors: HostSelectors,
    action: PaymentReduxAction,
    offerType?: OfferType
): PaymentWithDealerIdRequest => {
    const toggles: FeatureTogglesAndUrlOnly = state.toggles || state.featureToggles;
    // TODO: use dealer id only, the whole dealer is needed for calculating includeGovFees [rally story US916013]
    const dealer: MongoDealer & { id: number } = state.dealer;

    // To clean-up later: Already defined before
    const paymentOfferType = offerType || offerSelectors.getCurrentOfferType(state);
    const actionForPayment = action?.type || getActionForPayment(state) || '';
    const traceId = getTraceId(state) || state.traceId || uuid();
    const programIncentivesRestorationEnabled = isProgramIncentivesRestorationEnabled(state);
    const includeTaxesSetting = shouldIncludeTaxes(state);
    const vehicleOverride: Vehicle | undefined = getActionMetaValue(action, 'vehicleOverride');
    const vehicleState = vehicleOverride || offerSelectors.getVehicleDetails(state);
    const drivelineTrimHintFallback = hostSelectors.drivelineTrimHint && hostSelectors.drivelineTrimHint(state);
    const vehicle = createVehicleForPaymentNode(
        vehicleState,
        drivelineTrimHintFallback,
        isSendVehicleFieldsOnPaymentEnabled(state)
    );
    const shopperZip = offerSelectors.getShopperZip(state);
    const dealXgId = offerSelectors.getDealXgId(state);
    const dealXgVersion = offerSelectors.getDealXgVersion(state);
    const isRollToCall = getActionMetaValue(action, 'isRollToCall', false);
    const isProgramCall = getActionMetaValue(action, 'isProgramCall', false);
    const isUpdateSelectedIncentivesCall = (action?.type || '') === UPDATE_SELECTED_INCENTIVES;
    const pinGridToMainDeal = isRegularGridRowAction(action) && isGridIncentiveManagementEnabled(state, paymentOfferType);
    const includeStandardIncentives = !pinGridToMainDeal && resolveIncludeStandardIncentives(state, action, paymentOfferType);
    const actionMiddleware = getActionMetaValue(action, 'middleware') || {};
    const sourceApplication = getActionMetaValue(action, 'sourceApplication', '');
    const actionPaymentOverride: IPaymentOverride = getActionMetaValue(action, PAYMENT_REQUEST_OVERRIDE_NODE);
    const postStageTransformation: PostStageTransformationByType = getActionMetaValue(action, 'postStageTransformation');

    // TODO: remove this line when these toggles are deprecated
    // (then the logic will be equal to what we have in PO) [rally story US916013]
    const includeGovFees = getIncludeGovernmentFees(dealer);
    const outsideLienholder = offerSelectors.getOutsideLienholder(state);
    const outsideLienholderExists = isOutsideLienHolderData(outsideLienholder);

    const transferRegistrationDetails = offerSelectors.getTransferRegistrationDetails(state);
    const priorLeaseBalanceForPayment = offerSelectors.getPriorLeaseBalanceForPayment(state);
    // Prefer a caller-supplied lease equity allocation order override (Shopper Platform passes the DR-Admin
    // derived order on post-save payment calls) so the request uses it immediately without waiting for a
    // response to update state; otherwise fall back to the committed offer value.
    const equityAllocationMethodOverride = (actionPaymentOverride?.[LEASE] as { equityAllocationMethod?: string } | undefined)
        ?.equityAllocationMethod;
    const equityAllocationMethod = equityAllocationMethodOverride ?? offerSelectors.getEquityAllocationMethod(state);
    const paymentFrequencyForEquity = getPayloadPaymentFrequency(state, paymentOfferType);
    const isOplForEquity = paymentFrequencyForEquity === PaymentScheduleTypes.OnePayLease;
    // always fall back to including the trade

    const isManagerView = ApplicationCodes.MV === sourceApplication || hostSelectors?.isManagerView;
    const enableOverridesForSalesView = offerSelectors.getHonorOverrides(state, paymentOfferType);

    const nonSelectedTermHonoringSV = (enableOverridesForSalesView && actionMiddleware.nonSelectedTermHonoringSV) || false;
    // Extract skipProgramOverride flag for OPL auto-select lender functionality
    const skipProgramOverride = actionMiddleware.skipProgramOverride || false;
    const financeType = paymentOfferType === LEASE ? LEASE : FINANCE;
    const creditTier = offerSelectors.getSelectedCreditTierScoreByType(state, financeType);
    const quoteAllCreditTiersMeta = getActionMetaValue(action, 'quoteAllCreditTiers');
    const quoteAllCreditTiers: boolean | undefined =
        typeof quoteAllCreditTiersMeta === 'boolean' ? quoteAllCreditTiersMeta : undefined;
    const creditScoreOverride: number | undefined =
        quoteAllCreditTiers === true ? undefined : getActionMetaValue(action, 'creditScoreOverride');
    const creditScore = creditScoreOverride ?? (offerSelectors.getManualCreditScore(state) || getCreditScore(creditTier));
    const includeTaxes = resolveIncludeTaxes(state, shopperZip, includeTaxesSetting, isManagerView, paymentOfferType);

    const { gridAction = false, isMainPayment = false } = getActionMetaValue(action, 'paymentGrid') || {};
    const explicitLockApplies =
        Boolean(state.featureToggles?.[EXPLICIT_LENDER_P1_TOGGLE]) &&
        !isProgramCall &&
        !EXPLICIT_LOCK_EXEMPT_ACTIONS.has(action?.type) &&
        !(isKeepLenderOnZipChangeEnabled(state) && isZipChangingShopperApply(action)) &&
        !(gridAction && !isMainPayment);
    const explicitLockedProgram: GenericObject | undefined = explicitLockApplies
        ? getActionMetaValue(action, LOCKED_PROGRAM_META) ?? hostSelectors?.getExplicitLenderLock?.(state, paymentOfferType)
        : undefined;
    const isExplicitProgramLock = isValidProgram(explicitLockedProgram);
    const { lenderCode: explicitLockedLenderCode, ...explicitLockedPqProgram } = explicitLockedProgram ?? {};
    const contractingLenderCode = getContractingLenderCode(state, paymentOfferType);
    const lockNamesPricedLender = contractingLenderCode == null || contractingLenderCode === explicitLockedLenderCode;
    if (isExplicitProgramLock && lockNamesPricedLender && action) {
        setActionMetaValue(action, explicitLockedProgram, EXPLICIT_PROGRAM_LOCK_META);
    }

    const requestOptions: RequestOverrideOptions = {
        isRollToCall,
        isProgramCall,
        isManagerView,
        nonSelectedTermHonoringSV,
        enableOverridesForSalesView,
        skipProgramOverride: skipProgramOverride && !isExplicitProgramLock,
        // Carried so the overrides builders can tell a deliberately forced user-program-quotes call from an ordinary one.
        forceUseGenericCalcs: getActionMetaValue(action, 'forceUseGenericCalcs') === true,
        restoredColumnPrograms: getActionMetaValue(action, 'restoredColumnPrograms'),
        // A grid cell selection, read off the fact `forceEngineForSelectedCell` stamps rather than the term list.
        isGridCellSelection: getActionMetaValue(action, 'gridCellSelectionOfferType') === paymentOfferType,
        gridColumnIndices: getActionMetaValue(action, 'gridColumnIndices'),
        columnSellRate: getActionMetaValue(action, 'columnSellRate'),
        columnResidualInfo: getActionMetaValue(action, 'columnResidualInfo'),
        ignoreTermSellRateOverride: getActionMetaValue(action, 'ignoreTermSellRateOverride') === true,
        ignoreTermResidualOverride: getActionMetaValue(action, 'ignoreTermResidualOverride') === true,
        gridColumnIndex: singleGridColumnOf(getActionMetaValue(action, 'gridColumnIndices')),
        // A deliberate single-value Rate/residual override, keyed on edit origin (deal screen vs. grid cell) rather than grid design.
        isDealScreenSingleTermOverride: getActionMetaValue(action, 'isDealScreenSingleTermOverride') === true,
        lockedProgram: isExplicitProgramLock ? explicitLockedPqProgram : hostSelectors?.getLockedProgram?.(state, paymentOfferType),
        lockedLenderCode: isExplicitProgramLock ? explicitLockedLenderCode : undefined,
        isExplicitProgramLock,
        upqFallbackDisabled: Boolean(hostSelectors?.isUserProgramQuotesFallbackDisabled?.(state)),
        isFlexBuyGridCall: (action?.meta as GenericObject | undefined)?.paymentGrid?.isFlexBuyPayment === true
    };

    // A grid column may be priced on a different engine than the deal, so a call can force user-program-quotes
    // for its own terms without disturbing the deal's endpoint. Inert unless a caller sets the meta.
    const forcedUseGenericCalcs = getActionMetaValue(action, 'forceUseGenericCalcs');
    const keepUpqForSuppressedExcursion = getActionMetaValue(action, 'keepUpqForSuppressedExcursion') === true;
    const useGenericCalcs =
        typeof forcedUseGenericCalcs === 'boolean'
            ? forcedUseGenericCalcs
            : keepUpqForSuppressedExcursion
            ? true
            : shouldUseGenericCalcsStructure(state, paymentOfferType, hostSelectors, requestOptions);

    requestOptions.useGenericCalcs = useGenericCalcs;

    // Record which engine this request was built for so the response can be attributed without
    // re-deriving it. Stamped here because this is the single point where the lane is decided, honouring
    // the `forceUseGenericCalcs` override and the special cases above.
    setActionMetaValue(action, useGenericCalcs, 'usedGenericCalcs');

    // Confined to program-quotes: on user-program-quotes asking would suppress the
    // `appliedIncentiveIds` echo below and gain no itemization.
    const canReportChosenStandards = isRequestChosenStandardsEnabled(state) && includeStandardIncentives && !useGenericCalcs;
    const gridRowAsksForIncentives = isGridRowAskingForIncentives(state, action, paymentOfferType, useGenericCalcs);
    const resolvedIncludeIncentives = resolveIncludeIncentives(
        action,
        actionForPayment,
        programIncentivesRestorationEnabled,
        canReportChosenStandards,
        isRollToApplyIncentiveCatalogEnabled(state) && isRollToTermChange(action)
    );
    const includeIncentives = gridRowAsksForIncentives ? true : resolvedIncludeIncentives;
    setActionMetaValue(action, gridRowAsksForIncentives && !resolvedIncludeIncentives, GRID_ASK_TURNED_ON_INCENTIVES_META);

    // Only a program-quotes call can be retried as a user program, so a call already heading there carries nothing.
    requestOptions.useUpqRetry = !useGenericCalcs && !!state.featureToggles?.enableUpqRetry_F283308;
    requestOptions.useIncentivesOverrides =
        !useGenericCalcs && !pinGridToMainDeal && !!state.featureToggles?.US2127588_Enable_Per_Term_Conditional_Incentive_Narrowing;

    requestOptions.usePerTermIncentiveOverrides =
        !useGenericCalcs && !pinGridToMainDeal && !!state.featureToggles?.US2133348_Enable_Per_Term_Incentive_Overrides;
    requestOptions.isUpdateSelectedIncentivesCall = isUpdateSelectedIncentivesCall;
    requestOptions.includeStandardIncentives = includeStandardIncentives;

    requestOptions.enableOverridesForCreditDecision = isApplyingCreditDecision(action);

    const vdpUrl = getVdpUrl(state);
    const {
        includeDefaultDealerFees,
        includeDefaultGovFees,
        includeDefaultLenderFees,
        includeDefaultTaxes,
        includeCalculatedGovFees,
        includeDefaultDealerBasedTaxes
    } = getFeesAndTaxesSettings(state);

    const partnerId =
        (typeof hostSelectors?.getPartnerCode === 'function' ? hostSelectors.getPartnerCode(state) : undefined) ||
        offerSelectors.getPartnerId(state);
    const partnerDealerId =
        (typeof hostSelectors?.getPartnerDealerId === 'function' ? hostSelectors.getPartnerDealerId(state) : undefined) ||
        offerSelectors.getPartnerDealerId(state);

    const dealerAddress = getDealerAddress(state, dealer, toggles.passCustomerVersionedAddressToPS);
    const programEndDays = getProgramEndDays(state);

    // DE720579 (gated by overrideTaxFixes_US1984109): SV Payment Grid opens fire fresh PO calls
    // that previously omitted the manual-tax suppression (because the gate was isManagerView-only).
    // Mirror the MV branch for SV so a saved manualTotalTax / manualTaxItems is not overwritten by
    // default-tax recompute.
    const enableOverrideTaxFixes = state.featureToggles?.overrideTaxFixes_US1984109;
    const isSalesView = offerSelectors.getIsSalesView(state);
    const isManagerOrSalesView = enableOverrideTaxFixes ? isManagerView || isSalesView : isManagerView;
    // Scope to the request's offer type so SV mirrors MV. The unscoped selectors default to the
    // deal's current offer type — wrong when SV drives a LEASE payment while the deal is on Finance,
    // which made hasManualTotalTax false in SV and left includeTaxes=true (default-tax recompute
    // overwriting the override). No-op for the main grid where current offer type already matches.
    const hasManualTotalTax = enableOverrideTaxFixes
        ? offerSelectors.hasManualTotalTax(state, paymentOfferType)
        : offerSelectors.hasManualTotalTax(state);
    const hasManualTaxItems = enableOverrideTaxFixes
        ? offerSelectors.hasManualTaxItems(state, false, paymentOfferType)
        : offerSelectors.hasManualTaxItems(state);

    const includeFlatTaxes = isManagerOrSalesView && hasManualTotalTax;

    const includeManualTaxItems = isManagerOrSalesView && hasManualTaxItems;

    const includeManualTaxItemsForSV =
        enableOverridesForSalesView && offerSelectors.hasManualTaxItems(state, enableOverridesForSalesView, paymentOfferType);
    const includeNonFlatTaxes = includeDefaultTaxes ?? includeTaxes;

    const sponsor = offerSelectors.getSponsor(state);

    // Use calculated government fees if available, otherwise fall back to default government fees setting
    const effectiveIncludeGovFees = includeCalculatedGovFees ?? includeGovFees;

    const newRequest: PaymentWithDealerIdRequest = {
        dealerId: dealer.id,
        partnerId,
        partnerDealerId,
        vehicle,
        dealerAddress,
        payment: {
            creditScore,
            enableRTSCalculation: true,
            includeIncentives,
            showCompatibleincentives: programIncentivesRestorationEnabled,
            includeGovFees: isManagerView // TODO: Review if this is the correct fix or if we should apply the new logic only to DealStarter
                ? effectiveIncludeGovFees
                : Boolean(shopperZip) && effectiveIncludeGovFees,
            includeTaxes: includeFlatTaxes || includeManualTaxItems || includeManualTaxItemsForSV ? false : includeNonFlatTaxes,
            includeDefaultDealerFees,
            includeDefaultGovFees,
            includeDefaultLenderFees,
            includeDefaultDealerBasedTaxes,
            programEndDays,
            requestTypes: [] // an empty array means all allowed by dealer settings types
        },
        traceId,
        ...(postStageTransformation ?? {})
    };

    newRequest.metadata = {
        vdpUrl,
        sponsor: sponsor.toUpperCase(),
        ...(dealXgId ? { dealXgId } : {}),
        ...(dealXgVersion ? { dealXgVersion } : {}),
        actionForPayment,
        sourceApplication,
        // Preserve any metadata supplied via action.meta.postStageTransformation.metadata.
        // Old behavior (before US1946907): postStageTransformation was spread AFTER the
        // metadata object literal, so caller-supplied metadata keys won on conflict.
        // Keep that precedence now that metadata is assigned separately.
        ...((postStageTransformation as { metadata?: GenericObject<unknown> } | undefined)?.metadata ?? {})
    };

    if (quoteAllCreditTiers !== undefined) {
        (newRequest.payment as IPaymentOverride & { quoteAllCreditTiers?: boolean }).quoteAllCreditTiers = quoteAllCreditTiers;
    }

    if (transferRegistrationDetails?.registrationType) {
        newRequest.payment.registrationType = transferRegistrationDetails.registrationType;
    }
    if (transferRegistrationDetails?.registrationExpiresOn) {
        newRequest.payment.registrationExpiresOn = transferRegistrationDetails.registrationExpiresOn;
    }

    if (toggles.F300850_Add_Prior_Balance && priorLeaseBalanceForPayment && paymentOfferType !== CASH) {
        (newRequest.payment as IPaymentOverride & { priorLeaseBalance?: PriorLeaseBalanceForPayment }).priorLeaseBalance =
            priorLeaseBalanceForPayment;
    }

    if (
        ((toggles.enableEquityAllocation && !isManagerView) || (toggles.enableEquityAllocationOrder && isManagerView)) &&
        equityAllocationMethod
    ) {
        newRequest.payment.equityAllocationMethod =
            isOplForEquity && toggles.enableEquityAllocationOrder
                ? stripCashFromEquityAllocationMethod(equityAllocationMethod)
                : equityAllocationMethod;
    }

    if (paymentOfferType === LEASE && getIsEquityAllocationOrderEnabled(state, isManagerView)) {
        // Keep the explicit LEASE argument: an Alt Deal Structure slideout can drive a LEASE payment while
        // the deal is still on Finance, and the unscoped call would then read the FINANCE flag (same reason
        // as the read in setLeaseNode).
        newRequest.payment.isTargetCashAmountPaid = offerSelectors.getIsTargetOutOfPocket(state, LEASE);
    }

    if (paymentOfferType === CASH) {
        newRequest.payment.hasOutsideFinancing = outsideLienholderExists;
    }

    const shouldOmitCustomRateGroup = hostSelectors?.shouldOmitCustomRateGroup?.(state) || false;

    const customRateGroup = resolveCustomRateGroup({
        state,
        paymentOfferType,
        enableCustomRateCard: !!toggles.enableCustomRateCard,
        enableDealerRateCardSupport: !!toggles.enableDealerRateCardSupport,
        isManagerView: !!isManagerView,
        isSalesView,
        shouldOmitCustomRateGroup
    });

    if (customRateGroup) {
        newRequest.payment.customRateGroup = customRateGroup;
    }

    if (isManagerView && paymentOfferType === FINANCE) {
        newRequest.payment.prioritizeDdlp = false;
    }

    if (paymentOfferType === FINANCE) {
        const qualificationId =
            typeof hostSelectors?.getQualificationId === 'function' ? hostSelectors.getQualificationId(state) : undefined;
        if (qualificationId) {
            newRequest.payment.qualificationId = qualificationId;
        }
    }

    switch (paymentOfferType) {
        case CASH:
            setCashNode(
                state,
                newRequest.payment,
                isProgramCall,
                isManagerView,
                isUpdateSelectedIncentivesCall,
                includeStandardIncentives
            );
            break;

        case FINANCE:
            setFinanceNode(
                state,
                newRequest.payment,
                actionPaymentOverride,
                isProgramCall,
                isManagerView,
                isUpdateSelectedIncentivesCall,
                includeStandardIncentives
            );
            requestOptions.termList = newRequest.payment[FINANCE]?.termList;
            requestOptions.termMonths = actionPaymentOverride?.[FINANCE]?.termMonths;
            break;

        case LEASE:
            setLeaseNode(
                state,
                newRequest.payment,
                actionPaymentOverride,
                hostSelectors,
                isProgramCall,
                isManagerView,
                isUpdateSelectedIncentivesCall,
                includeStandardIncentives
            );
            requestOptions.termList = newRequest.payment[LEASE]?.termList;
            requestOptions.termMonths = actionPaymentOverride?.[LEASE]?.termMonths;
            break;
        case BALLOON:
            setBalloonNode(
                state,
                newRequest.payment,
                actionPaymentOverride,
                isProgramCall,
                isManagerView,
                isUpdateSelectedIncentivesCall,
                includeStandardIncentives
            );
            requestOptions.termList = newRequest.payment[BALLOON]?.termList;
            requestOptions.termMonths = actionPaymentOverride?.[BALLOON]?.termMonths;
            break;
    }

    setRequestOverrides(state, newRequest.payment, paymentOfferType, action, requestOptions);

    if (isShopperLocationChangeCall(action)) {
        dropStandardsForLocationChange(newRequest.payment, paymentOfferType);
    }

    if (requestOptions.useGenericCalcs) {
        newRequest.payment.useGenericCalcs = true;
        const encryptedPaymentData = offerSelectors.getEncryptionPaymentData(state);
        if (encryptedPaymentData) {
            newRequest.metadata[OFFER_ENCRYPTION_FIELDS.PAYMENT] = encryptedPaymentData;
        }
    }

    if (requestOptions.useGenericCalcs && !includeIncentives && state.featureToggles?.enableSuppliedAppliedIncentives) {
        const offerNode = newRequest.payment[paymentOfferType] as (GenericObject & { appliedIncentiveIds?: number[] }) | undefined;
        const appliedIncentiveIds = buildAppliedIncentiveIds(state, paymentOfferType, isManagerView);
        if (appliedIncentiveIds && offerNode) {
            offerNode.appliedIncentiveIds = appliedIncentiveIds;
        }
    }

    const overridesExtraData = offerSelectors.getEnctyptedOverridesByOfferType(state, paymentOfferType);

    if (overridesExtraData && requestOptions.useGenericCalcs) {
        newRequest.metadata[OFFER_ENCRYPTION_FIELDS.OVERRIDES] = overridesExtraData;
    }

    const dealerCash = offerSelectors.getEncryptedIncentivesByOfferType(state, paymentOfferType);

    if (dealerCash && enableOverridesForSalesView) {
        newRequest.metadata[OFFER_ENCRYPTION_FIELDS.INCENTIVES] = dealerCash;
    }

    // Only a host that receives these encrypted carries them; Manager View sends the plain fields.
    const encryptedInvoiceAmount = offerSelectors.getEncryptedVehicleInvoiceAmount(state);

    if (encryptedInvoiceAmount) {
        newRequest.metadata[VEHICLE_ENCRYPTION_FIELDS.INVOICE_AMOUNT] = encryptedInvoiceAmount;
    }

    const encryptedVehicleCost = offerSelectors.getEncryptedVehicleCost(state);

    if (encryptedVehicleCost) {
        newRequest.metadata[VEHICLE_ENCRYPTION_FIELDS.COST] = encryptedVehicleCost;
    }

    // Add profitability data if available
    addProfitabilityDataToPayload(state, newRequest, paymentOfferType);

    mapTradeInDetails(state, newRequest, hostSelectors, isRollToCall);
    setLocationId(state, newRequest.payment);
    setShopper(state, newRequest);

    if (hostSelectors?.getPaymentProducts) {
        setRequestMenuItems(
            state,
            newRequest.payment[paymentOfferType] as IPaymentOverrideCash | IPaymentOverrideFinance | IPaymentOverrideLease,
            hostSelectors
        );
    } else {
        setRequestMenuSelectionsId(
            state,
            newRequest.payment[paymentOfferType] as IPaymentOverrideCash | IPaymentOverrideFinance | IPaymentOverrideLease,
            hostSelectors
        );
    }

    return newRequest;
};

export const createVehicleForPaymentNode = (
    vehicleState: Vehicle,
    drivelineFallback?: string,
    sendVehicleFields = false
): IVehicleForPayment => {
    const {
        vin,
        type: condition,
        mileage,
        retailPrice,
        listedPrice,
        styleId = null,
        year,
        invoiceAmount,
        unladenWeight,
        trim,
        cost,
        driveLine,
        make,
        model,
        msrp,
        discountOfMsrp,
        certifiedUsed
    } = vehicleState;
    let chromeStyleId;
    if (styleId) {
        chromeStyleId = Number(styleId);
    } else if (vehicleState.chromeStyleId) {
        chromeStyleId = Number(vehicleState.chromeStyleId);
    }

    let trimValue = trim;
    const driveLineValue = driveLine || drivelineFallback;
    if (trimValue && driveLineValue) {
        trimValue = `${trimValue} ${driveLineValue}`;
    }

    return {
        vin: vin === '' ? undefined : vin,
        condition: condition as AccelerateVehicleCondition,
        mileage: Number(mileage),
        retailPrice: Number(retailPrice || listedPrice),
        listedPrice: Number(listedPrice),
        chromeStyleId,
        year: Number(year),
        ...(invoiceAmount != null && { invoiceAmount: Number(invoiceAmount) }),
        ...(unladenWeight && unladenWeight >= 0 && { unladenWeight: Number(unladenWeight) }),
        ...(trimValue && { trim: trimValue }),
        ...(cost && { vehicleCost: Number(cost) }),
        ...(sendVehicleFields && {
            ...(make && { make }),
            ...(model && { model }),
            ...(msrp != null && { msrp: Number(msrp) }),
            ...(discountOfMsrp != null && { discountOfMsrp: Number(discountOfMsrp) }),
            ...(certifiedUsed != null && { certifiedUsed })
        })
    } as IVehicleForPayment;
};

export const createVehicleNode = (vehicleState: Vehicle): IApsVehicleNode => {
    const { vin, type: condition, mileage, retailPrice, listedPrice, styleId = null, year } = vehicleState;
    let chromeStyleId: number | undefined = undefined;
    if (styleId) {
        chromeStyleId = Number(styleId);
    }

    return {
        vin: vin || '',
        condition: condition || '',
        mileage: Number(mileage),
        retailPrice: Number(retailPrice || listedPrice),
        chromeStyleId,
        year: Number(year)
    };
};

/**
 * Gets the default down payment for a given offer type.
 *
 * @param state - The Redux state object
 * @param offerType - The type of offer (CASH, FINANCE, or LEASE)
 * @param override - Optional override value:
 *   - If `null`, returns `undefined` (explicitly no down payment)
 *   - If a number, returns that number
 *   - If `undefined`, falls back to state value or 0
 *
 * @returns The down payment amount or undefined
 */
export const getDefaultDownPayment = (state: GenericObject, offerType: OfferType, override?: null | number): number | undefined => {
    if (override === null) {
        return undefined;
    }

    if (override !== undefined) {
        return override;
    }

    return offerSelectors.getDownPaymentByOfferType(state, offerType) ?? 0;
};

const getOnePayLeaseDownPayment = (
    state: GenericObject,
    offerType: OfferType,
    override: number | undefined,
    isEquityAllocationOrderEnabled: boolean,
    isManagerView: boolean
): number | undefined => {
    if (!isEquityAllocationOrderEnabled) {
        const keepsCashDown = isManagerView && Boolean(state.featureToggles?.DE780047_Enable_One_Pay_Keep_Cash_Down);
        return keepsCashDown && override === undefined ? offerSelectors.getDownPaymentByOfferType(state, offerType) : override;
    }

    return override === undefined
        ? offerSelectors.getDownPaymentByOfferType(state, offerType)
        : getDefaultDownPayment(state, offerType, override);
};

const getAppliedConditionalIdsForProgramCall = (state: GenericObject, offerType: OfferType): number[] =>
    offerSelectors
        .getSelectedConditionalIncentivesToDisplay(state, offerType)
        .map((incentive: GenericObject) => incentive.pdId)
        .filter((id: unknown): id is number => typeof id === 'number' && !Number.isNaN(id));

const buildStandardsNode = (cleanConditional: number[], standardIds: number[]): IIncentives =>
    standardIds.length > 0 ? { conditional: cleanConditional, nonConditional: standardIds } : { conditional: cleanConditional };

const buildManagerViewIncentivesNode = (
    cleanConditional: number[],
    nonConditional: number[],
    appliedTermStandardIds: number[],
    hasRecordedSelection: boolean,
    includeStandardIncentives: boolean
): IIncentives => {
    if (includeStandardIncentives) {
        return { conditional: cleanConditional };
    }
    return { conditional: cleanConditional, nonConditional: hasRecordedSelection ? nonConditional : appliedTermStandardIds };
};

const resolveEffectiveConditional = (
    state: GenericObject,
    offerType: OfferType,
    conditional: number[],
    isProgramCall: boolean,
    isManagerView: boolean,
    isUpdateSelectedIncentivesCall: boolean
): number[] => {
    if (isProgramCall) {
        return getAppliedConditionalIdsForProgramCall(state, offerType);
    }
    if (isUpdateSelectedIncentivesCall && !isManagerView) {
        return conditional;
    }
    return !offerSelectors.hasRecordedConditionalSelection(state, offerType)
        ? getAppliedConditionalIdsForProgramCall(state, offerType)
        : conditional;
};

const toStandardIds = (ids: unknown, isStandard: (id: number) => boolean): number[] =>
    ((ids as unknown[]) ?? []).filter((id): id is number => typeof id === 'number' && !Number.isNaN(id)).filter(isStandard);

// DE771157: a grid cash-down edit points the selected term at an id this request creates, so the applied
// set reads empty and `nonConditional: []` tells Payment Services to apply zero standards.
const resolveAppliedStandardIds = (state: GenericObject, offerType: OfferType, isStandard: (id: number) => boolean): number[] => {
    if (offerType === CASH && isCashSwitchStandardRebateEnabled(state)) {
        return toStandardIds(offerSelectors.getOfferDetails(state, CASH).appliedIncentiveIds, isStandard);
    }
    const fromSelected = toStandardIds(offerSelectors.getAppliedIncentiveIdsForSelectedTerm(state, offerType), isStandard);
    if (fromSelected.length > 0 || !isUnpricedTermStandardsCarryEnabled(state)) return fromSelected;

    const selectedTermId = offerSelectors.getTermIdForOfferType(state.offer, offerType);
    const termsById = offerSelectors.getAllTermsFromOffer(state.offer);
    // A term that exists and reports none is a real answer, not a gap.
    if (selectedTermId == null || termsById[selectedTermId] != null) return fromSelected;

    const months = parseSelectedTermMonths(selectedTermId);
    if (months == null) return fromSelected;

    // Not `getTermByMonthFromOffer`: it answers with the first row for these months, which can be
    // another row this same request is still creating.
    for (const termId of offerSelectors.getTermsIdByOfferTerms(state.offer, offerType)) {
        const term = termsById[termId];
        if (term?.months !== months) continue;
        const standards = toStandardIds(term.appliedIncentiveIds, isStandard);
        if (standards.length > 0) return standards;
    }
    return fromSelected;
};

type IncentiveCandidates = {
    conditional: number[];
    standardIdSet: Set<number>;
    availableStandardIds: number[];
    penciledStandardIds: number[];
    hasRecordedSelection: boolean;
};

type IncentiveFlags = {
    isProgramCall: boolean;
    isManagerView: boolean;
    includeStandardIncentives: boolean;
    honorsDealerStandardExclusion: boolean;
};

const readIncentiveCandidates = (
    state: GenericObject,
    offerType: OfferType,
    conditional: number[],
    isProgramCall: boolean,
    isManagerView: boolean,
    isUpdateSelectedIncentivesCall: boolean
): IncentiveCandidates => {
    const effectiveConditional = resolveEffectiveConditional(
        state,
        offerType,
        conditional,
        isProgramCall,
        isManagerView,
        isUpdateSelectedIncentivesCall
    );
    const availableStandardIds = offerSelectors.getMappedIdsForAvailableNonConditionalIncentivesByOfferType(state, offerType);
    const penciledStandardIds = offerSelectors.getSelectedNonConditionalIdsFromPenciledInputs(state, offerType);
    const standardIdSet = new Set([
        ...availableStandardIds,
        ...penciledStandardIds,
        ...offerSelectors.getCatalogNonConditionalIdsByOfferType(state, offerType)
    ]);
    const overriddenIds = offerSelectors.getOverriddenProgramIds(state, offerType);
    return {
        conditional: effectiveConditional.filter((id) => !standardIdSet.has(id) && !overriddenIds.has(id)),
        standardIdSet,
        availableStandardIds,
        penciledStandardIds,
        hasRecordedSelection: offerSelectors.hasRecordedNonConditionalSelection(state, offerType)
    };
};

const buildIncentivesFromCandidates = (
    candidates: IncentiveCandidates,
    flags: IncentiveFlags,
    isCompatible: (id: number) => boolean,
    appliedStandardIds: number[]
): IIncentives => {
    const cleanConditional = candidates.conditional.filter(isCompatible);
    const nonConditional = candidates.penciledStandardIds.filter(isCompatible);
    if (flags.isManagerView) {
        return buildManagerViewIncentivesNode(
            cleanConditional,
            nonConditional,
            appliedStandardIds,
            candidates.hasRecordedSelection,
            flags.includeStandardIncentives
        );
    }
    if (flags.honorsDealerStandardExclusion) {
        return { conditional: cleanConditional };
    }
    if (!candidates.hasRecordedSelection) {
        return buildStandardsNode(cleanConditional, candidates.availableStandardIds.filter(isCompatible));
    }
    if (flags.isProgramCall && nonConditional.length === 0) {
        return { conditional: cleanConditional };
    }
    return { conditional: cleanConditional, nonConditional };
};

const buildProgramIncentivesNode = (
    state: GenericObject,
    offerType: OfferType,
    conditional: number[],
    isProgramCall: boolean,
    isManagerView: boolean,
    isUpdateSelectedIncentivesCall: boolean,
    includeStandardIncentives: boolean
): IIncentives => {
    if (!isProgramIncentivesRestorationEnabled(state)) {
        return { conditional };
    }
    if (isManagerView && isProgramCall) {
        return { conditional: [] };
    }
    const candidates = readIncentiveCandidates(
        state,
        offerType,
        conditional,
        isProgramCall,
        isManagerView,
        isUpdateSelectedIncentivesCall
    );
    const keepsDealIncentives = isManagerView && isGridIncentiveManagementEnabled(state, offerType);
    const termId = offerSelectors.getTermIdForOfferType(state.offer, offerType);
    const compatibleIds =
        termId != null && !keepsDealIncentives ? offerSelectors.getCompatibleIncentiveIdsForTerm(state, termId) : null;
    const editedIds = keepsDealIncentives ? offerSelectors.getEditedIncentiveIds(state, offerType) : null;
    const isCompatible = (id: number): boolean => (compatibleIds == null || compatibleIds.has(id)) && !editedIds?.has(id);
    const isFetchedStandard = (id: number) => candidates.standardIdSet.has(id) && isCompatible(id);
    const fetchedStandardIds = offerSelectors.areProgramStandardsFetched(state, offerType)
        ? offerSelectors.getFetchedProgramStandardCodes(state, offerType).filter(isFetchedStandard)
        : [];
    const appliedStandardIds =
        fetchedStandardIds.length > 0 ? fetchedStandardIds : resolveAppliedStandardIds(state, offerType, isFetchedStandard);
    return buildIncentivesFromCandidates(
        candidates,
        {
            isProgramCall,
            isManagerView,
            includeStandardIncentives,
            honorsDealerStandardExclusion: offerSelectors.shouldHonorDealerStandardExclusion(state, offerType)
        },
        isCompatible,
        appliedStandardIds
    );
};

export const withoutEditedIncentives = (state: GenericObject, offerType: OfferType, incentives: IIncentives): IIncentives => {
    if (!isGridIncentiveManagementEnabled(state, offerType)) {
        return incentives;
    }
    const edited = offerSelectors.getEditedIncentiveIds(state, offerType);
    if (edited.size === 0) {
        return incentives;
    }
    const isUnedited = (id: number) => !edited.has(id);
    return {
        ...incentives,
        conditional: incentives.conditional?.filter(isUnedited),
        ...(Array.isArray(incentives.nonConditional) ? { nonConditional: incentives.nonConditional.filter(isUnedited) } : {})
    } as IIncentives;
};

const buildOfferIncentivesNode = (
    state: GenericObject,
    offerType: OfferType,
    conditional: number[],
    isProgramCall: boolean,
    isManagerView: boolean,
    isUpdateSelectedIncentivesCall: boolean,
    includeStandardIncentives: boolean
): IIncentives =>
    withoutEditedIncentives(
        state,
        offerType,
        buildProgramIncentivesNode(
            state,
            offerType,
            conditional,
            isProgramCall,
            isManagerView,
            isUpdateSelectedIncentivesCall,
            includeStandardIncentives
        )
    );

export const getDealIncentiveIdsForRequest = (
    state: GenericObject,
    offerType: OfferType
): { conditional: number[]; nonConditional: number[] } => {
    const selectedIncentives = offerSelectors.getMappedIdsForSelectedIncentivesByOfferType(state, offerType);
    const node = buildOfferIncentivesNode(state, offerType, selectedIncentives, false, true, false, false);
    return { conditional: node.conditional as number[], nonConditional: node.nonConditional ?? [] };
};

const resolveExcludeStdIncs = (
    state: GenericObject,
    offerType: OfferType,
    incentives: IIncentives,
    isManagerView: boolean,
    override?: boolean
): { excludeStdIncs?: boolean } => {
    if (!isManagerView && offerSelectors.shouldHonorDealerStandardExclusion(state, offerType)) {
        return { excludeStdIncs: true };
    }
    if (!isManagerView || !isProgramIncentivesRestorationEnabled(state)) {
        return {};
    }

    return { excludeStdIncs: override ?? Array.isArray(incentives.nonConditional) };
};

export const getPayloadPaymentFrequency = (state: GenericObject, offerType: OfferType): PaymentSchedule | undefined => {
    const paymentFrequency = offerSelectors.getOfferPaymentFrequency(state) || offerSelectors.getPaymentFrequency(state, true);

    if (paymentFrequency) return paymentFrequency;

    const prevOfferType = parseTermId(offerSelectors.getPaymentTermId(state)).offerType;
    return offerType === prevOfferType ? offerSelectors.getLastPaymentFrequency(state) : undefined;
};

export const setCashNode = (
    state: GenericObject,
    requestPaymentNode: IPaymentOverride,
    isProgramCall = false,
    isManagerView = false,
    isUpdateSelectedIncentivesCall = false,
    includeStandardIncentives = false
): void => {
    const offerType = CASH;
    const { offerPrice, quotesExpansionList } = offerSelectors.getOfferDetails(state, offerType);

    const selectedIncentives = offerSelectors.getMappedIdsForSelectedIncentivesByOfferType(state, offerType) || [];
    const programSelectedIncentives = programSelectors.getSelectedIncentives(state) || [];

    const mergedSelectedIncentives = Array.from(new Set([...selectedIncentives, ...programSelectedIncentives]));

    // US1988502: forward the user-entered Cash Down as `holdDeposit` so Payment Services
    // nets it out of Total Balance Due. Mirrors setFinanceNode/setLeaseNode which always
    // forward `downPayment`.
    const holdDeposit = offerSelectors.getDealDeposit(state);

    const incentives = buildOfferIncentivesNode(
        state,
        offerType,
        mergedSelectedIncentives,
        isProgramCall,
        isManagerView,
        isUpdateSelectedIncentivesCall,
        includeStandardIncentives
    );

    requestPaymentNode.requestTypes?.push(RequestType.CASH);
    requestPaymentNode[offerType] = {
        offerPrice,
        incentives,
        ...resolveExcludeStdIncs(state, offerType, incentives, isManagerView),
        quotesExpansionList,
        holdDeposit
    };
};

export const setFinanceNode = (
    state: GenericObject,
    requestPaymentNode: IPaymentOverride,
    actionPaymentOverride: IPaymentOverride = {},
    isProgramCall = false,
    isManagerView = false,
    isUpdateSelectedIncentivesCall = false,
    includeStandardIncentives = false
): void => {
    const offerType = FINANCE;
    const paymentOverride = actionPaymentOverride[offerType] || {};
    const {
        offerPrice: financeOfferPrice,
        quotesExpansionList,
        specialPaymentFormula,
        initialPaymentDiscount
    } = offerSelectors.getOfferDetails(state, offerType);

    const selectedIncentives = offerSelectors.getMappedIdsForSelectedIncentivesByOfferType(state, offerType) || [];
    const downPayment = getDefaultDownPayment(state, offerType, paymentOverride.downPayment);
    const termList = paymentOverride.termList || getTermsListByStrategy(state, offerType, paymentOverride.termMonths);
    const daysToFirstPayment = offerSelectors.getDaysToFirstPayment(state);
    const selectedTerm = paymentOverride.termMonths || offerSelectors.getSelectedTermMonths(state, offerType);
    const contractDate = offerSelectors.getContractDate(state);
    const paymentFrequency = getPayloadPaymentFrequency(state, offerType);

    const incentives = buildOfferIncentivesNode(
        state,
        offerType,
        selectedIncentives,
        isProgramCall,
        isManagerView,
        isUpdateSelectedIncentivesCall,
        includeStandardIncentives
    );

    requestPaymentNode.requestTypes?.push(RequestType.FINANCE);
    requestPaymentNode.finance = {
        contractDate,
        termList,
        offerPrice: financeOfferPrice,
        incentives,
        ...resolveExcludeStdIncs(state, offerType, incentives, isManagerView, paymentOverride.excludeStdIncs),
        downPayment,
        selectedTerm,
        paymentFrequency,
        daysToFirstPayment,
        quotesExpansionList,
        specialPaymentFormula
    };

    setFlexBuyPaymentGrid({
        state,
        paymentOverride,
        initialPaymentDiscount: initialPaymentDiscount as number[],
        requestPaymentNode
    });
};

export const setLeaseNode = (
    state: GenericObject,
    requestPaymentNode: IPaymentOverride,
    actionPaymentOverride: IPaymentOverride = {},
    hostSelectors?: HostSelectors,
    isProgramCall = false,
    isManagerView = false,
    isUpdateSelectedIncentivesCall = false,
    includeStandardIncentives = false
): void => {
    const offerType = LEASE;
    const paymentOverride = actionPaymentOverride[offerType] || {};
    const annualMiles = offerSelectors.getInputAnnualMiles(state);
    const { offerPrice: leaseOfferPrice, quotesExpansionList } = offerSelectors.getOfferDetails(state, offerType);

    const selectedIncentives = offerSelectors.getMappedIdsForSelectedIncentivesByOfferType(state, offerType) || [];
    const paymentFrequency = getPayloadPaymentFrequency(state, offerType);
    const isOnePayLease = paymentFrequency === PaymentScheduleTypes.OnePayLease;
    const isEquityAllocationOrderEnabled = getIsEquityAllocationOrderEnabled(state, isManagerView);
    const clearsDownPayment = (paymentOverride.downPayment as number | null | undefined) === null;
    const downPaymentOverride = isEquityAllocationOrderEnabled && clearsDownPayment ? undefined : paymentOverride.downPayment;
    const downPayment = isOnePayLease
        ? getOnePayLeaseDownPayment(state, offerType, downPaymentOverride, isEquityAllocationOrderEnabled, isManagerView)
        : getDefaultDownPayment(state, offerType, downPaymentOverride);
    const termList = paymentOverride.termList || getTermsListByStrategy(state, offerType, paymentOverride.termMonths);
    const daysToFirstPayment = offerSelectors.getDaysToFirstPayment(state);
    const selectedTerm = paymentOverride.termMonths || offerSelectors.getSelectedTermMonths(state, offerType);
    // Read the LEASE flag explicitly (not the deal's current offer type). When the Alt Deal Structure
    // slideout drives a LEASE payment while the deal is still on Finance, the unscoped call would pick
    // up the FINANCE offer's isTargetOutOfPocket and contaminate the lease payload. No-op for the main
    // lease grid, where the current offer type is already LEASE.
    const isTargetOutOfPocket = offerSelectors.getIsTargetOutOfPocket(state, offerType);
    const isPaymentGridEnabled = offerSelectors.getPaymentGridToggle(state, LEASE);
    const isDueAtSigninAmountDeduct = hostSelectors?.getIsDueAtSigninAmountDeduct?.(state);
    const secondPaymentDueDate = offerSelectors.getSecondPaymentDueDate(state);
    const isPorscheProRataEnabled = state.featureToggles?.enablePorscheProRata;

    const incentives = buildOfferIncentivesNode(
        state,
        offerType,
        selectedIncentives,
        isProgramCall,
        isManagerView,
        isUpdateSelectedIncentivesCall,
        includeStandardIncentives
    );

    requestPaymentNode.requestTypes?.push(RequestType.LEASE);
    requestPaymentNode[offerType] = {
        termList,
        offerPrice: leaseOfferPrice,
        incentives,
        ...resolveExcludeStdIncs(state, offerType, incentives, isManagerView, paymentOverride.excludeStdIncs),
        downPayment,
        annualMiles,
        selectedTerm,
        paymentFrequency,
        daysToFirstPayment,
        ...(!isEquityAllocationOrderEnabled && { isTargetOutOfPocket: isTargetOutOfPocket || isPaymentGridEnabled }),
        quotesExpansionList
    };
    if (secondPaymentDueDate && isPorscheProRataEnabled) {
        requestPaymentNode[offerType]!.secondPaymentDueDate = secondPaymentDueDate;
    }
    if (isDueAtSigninAmountDeduct) {
        requestPaymentNode[offerType]!.deductDueAtSigning = isDueAtSigninAmountDeduct;
    }
};

const isTermsListEmpty = (termsList: number[] | undefined): boolean => {
    return !termsList?.length || termsList[0] == null;
};

//TODO: US927086 Adding balloon offer type to payment payload to handle error as of now
export const setBalloonNode = (
    state: GenericObject,
    requestPaymentNode: IPaymentOverride,
    actionPaymentOverride: IPaymentOverride = {},
    isProgramCall = false,
    isManagerView = false,
    isUpdateSelectedIncentivesCall = false,
    includeStandardIncentives = false
): void => {
    const offerType = BALLOON;
    const paymentOverride = actionPaymentOverride[offerType] || {};
    const annualMiles = offerSelectors.getInputAnnualMiles(state);
    const { offerPrice: balloonOfferPrice, quotesExpansionList } = offerSelectors.getOfferDetails(state, offerType);

    const selectedIncentives = offerSelectors.getMappedIdsForSelectedIncentivesByOfferType(state, offerType) || [];
    const downPayment = getDefaultDownPayment(state, offerType, paymentOverride.downPayment);
    const daysToFirstPayment = offerSelectors.getDaysToFirstPayment(state);

    // As no terms are defined for balloon deal on dr-admin, using finance terms as fallback to avoid blank terms in balloon deal.
    const balloonTerms = getTermsListByStrategy(state, BALLOON, paymentOverride.termMonths);
    const localOfferType = offerType === BALLOON && isTermsListEmpty(balloonTerms) ? FINANCE : offerType;
    const termList =
        paymentOverride.termList ??
        (localOfferType === BALLOON ? balloonTerms : getTermsListByStrategy(state, FINANCE, paymentOverride.termMonths));
    const selectedTerm = paymentOverride.termMonths || offerSelectors.getSelectedTermMonths(state, localOfferType);
    const paymentFrequency = getPayloadPaymentFrequency(state, offerType);

    (requestPaymentNode.requestTypes as (RequestType | ExtendedRequestType)[])?.push(ExtendedRequestType.BALLOON);
    requestPaymentNode[offerType] = {
        termList,
        offerPrice: balloonOfferPrice,
        incentives: buildOfferIncentivesNode(
            state,
            offerType,
            selectedIncentives,
            isProgramCall,
            isManagerView,
            isUpdateSelectedIncentivesCall,
            includeStandardIncentives
        ),
        downPayment,
        annualMiles,
        selectedTerm,
        paymentFrequency,
        daysToFirstPayment,
        quotesExpansionList
    };
};
export const getCashOverrides = (
    state: GenericObject,
    options: RequestOverrideOptions = {},
    _action?: PaymentReduxAction
): IPaymentServicesOverridesCash => {
    const offerType = CASH;
    const retailPriceOverride = offerSelectors.getRetailPriceOverrideByOfferType(state, offerType);
    const manualIncentives = getManualIncentivesRequestNode(state, options);
    const manualTotalTax = offerSelectors.getManualTotalTax(state, offerType);
    const enableOverridesForSV = options.enableOverridesForSalesView || false;
    // DE720579 (gated by overrideTaxFixes_US1984109): make SV mimic MV by sending the saved
    // manualTotalTax so Payment Services doesn't recompute default taxes over the override.
    const enableOverrideTaxFixes = state.featureToggles?.overrideTaxFixes_US1984109;
    const includeManualTotalTax = enableOverrideTaxFixes || !enableOverridesForSV;

    return {
        ...(manualTotalTax != null && includeManualTotalTax ? { manualTotalTax } : undefined),
        ...(manualIncentives != null
            ? {
                  manualIncentives
              }
            : undefined),
        ...(retailPriceOverride != null ? { retailPrice: retailPriceOverride } : undefined)
    };
};

const isVehicleSwitchPaymentAction = (action: PaymentReduxAction): boolean => action?.type === SWITCH_VEHICLE;

export const isShopperLocationChangeCall = (action: PaymentReduxAction): boolean =>
    getActionMetaValue(action, SHOPPER_LOCATION_CHANGE_META, false) === true;

const withoutStandards = (incentives: IIncentives | undefined): IIncentives | undefined => {
    if (!incentives || !('nonConditional' in incentives)) {
        return incentives;
    }
    const kept = { ...incentives };
    delete kept.nonConditional;
    return kept;
};

export const dropStandardsForLocationChange = (requestPaymentNode: IPaymentOverride, offerType: OfferType): void => {
    const offerNode = requestPaymentNode[offerType] as GenericObject | undefined;
    if (!offerNode) {
        return;
    }
    offerNode.incentives = withoutStandards(offerNode.incentives);
    const byTerm = offerNode.overrides?.byTerm as Record<string, GenericObject> | undefined;
    Object.values(byTerm ?? {}).forEach((termOverride) => {
        if (termOverride?.incentives) {
            termOverride.incentives = withoutStandards(termOverride.incentives);
        }
    });
};

const buildVehicleSwitchManualIncentivesOverride = (
    state: GenericObject,
    action: PaymentReduxAction,
    options: RequestOverrideOptions
): GenericObject => {
    if (!isVehicleSwitchPaymentAction(action) || !options.isManagerView || !isProgramIncentivesRestorationEnabled(state)) {
        return {};
    }
    const manualIncentives = getManualIncentivesRequestNode(state, options);
    return manualIncentives ? { manualIncentives } : {};
};

export const getNonCashOverrides = (
    state: GenericObject,
    action: PaymentReduxAction,
    requestOfferType: OfferType,
    termList: number[] = [],
    options: RequestOverrideOptions = {}
): GenericObject => {
    const offerType = requestOfferType || offerSelectors.getCurrentOfferType(state);
    // Read the request's offer type explicitly for every offer-scoped flag below. The unscoped
    // selectors default to the deal's current offer type — wrong when the Alt Deal Structure slideout
    // drives a LEASE payment while the deal is on Finance (they would pick up the FINANCE offer's
    // values). No-op for the main lease grid, where the current offer type is already LEASE.
    const manualTotalTax = offerSelectors.getManualTotalTax(state, offerType);
    const taxOverrideUpfront = offerSelectors.getTaxOverrideUpfront(state, offerType);
    const isCapitalizeFirstPayment = offerSelectors.getCapitalizeFirstPayment(state, offerType);
    // OPL requires full upfront payment - default capitalizeFirstPayment to false when not set in state
    const paymentFrequency = getPayloadPaymentFrequency(state, offerType);
    const isOnePayLease = paymentFrequency === PaymentScheduleTypes.OnePayLease;
    const capitalizeFirstPayment = isCapitalizeFirstPayment ?? (isOnePayLease ? false : undefined);

    const excludeOverrides = getActionMetaValue(action, 'excludeOverrides');
    // A discovery lookup carries no overrides at all. Kept separate from `excludeOverrides`, which does not mean
    // "send none" and is read elsewhere for unrelated decisions.
    const dropOverridesForDiscovery = getActionMetaValue(action, 'dropOverridesForDiscovery');
    const isTermList = termList.length;

    let overrides = {};

    const shouldApplyOverrides =
        doOverridesExistForOfferType(state, offerType, action, options) || options.nonSelectedTermHonoringSV;

    // Check for required fields if using UPQ and useGenericCalcs
    // Skip fallback to PQ if the last payment call already failed with missing UPQ fields — retrying PQ won't help
    const isUsingGenericCalcs = options.useGenericCalcs;
    const isMissingRequiredUPQFields = !hasRequiredFieldsForUPQ(state, offerType, isTermList ? termList : undefined);
    const lastPaymentMissingUPQFields = getLastPaymentMissingUPQFields(state);
    const dealerForcesUpq = offerSelectors.getDealerForcedUpq(state);
    const enableLeaseResidualTreatment = !!state.featureToggles?.enableLeaseResidualTreatment_F286227;
    const leaseInputsForResidual = offerType === LEASE ? offerSelectors.getOfferDetails(state, LEASE) : undefined;
    const leaseResidualMissing =
        offerType === LEASE &&
        isLeaseResidualMissing({
            baseResidualAmount: leaseInputsForResidual?.baseResidualAmountOverride,
            baseResidualPercent: leaseInputsForResidual?.baseResidualPercentOverride,
            adjustedResidualAmount: leaseInputsForResidual?.adjustedResidualAmountOverride,
            adjustedResidualPercent: leaseInputsForResidual?.adjustedResidualPercentOverride
        });
    const keepUpqForLeaseMissingResidual =
        enableLeaseResidualTreatment && isUsingGenericCalcs && leaseResidualMissing && !dealerForcesUpq;

    // A forced column has no override on the deal's selected term, which would read as "missing required UPQ
    // fields" and downgrade the call back to the engine that cannot price it. Force wins.
    const forcedUseGenericCalcs = getActionMetaValue(action, 'forceUseGenericCalcs') === true;
    const keepUpqForSuppressedExcursion = getActionMetaValue(action, 'keepUpqForSuppressedExcursion') === true;

    const shouldDowngradeToPQ =
        isUsingGenericCalcs &&
        isMissingRequiredUPQFields &&
        !lastPaymentMissingUPQFields &&
        !dealerForcesUpq &&
        !forcedUseGenericCalcs &&
        !keepUpqForSuppressedExcursion &&
        !keepUpqForLeaseMissingResidual;

    if (keepUpqForLeaseMissingResidual || (keepUpqForSuppressedExcursion && isMissingRequiredUPQFields)) {
        action.meta = {
            ...action.meta,
            missingRequiredUPQFields: true
        };
    } else if (shouldDowngradeToPQ) {
        action.meta = {
            ...action.meta,
            missingRequiredUPQFields: true
        };
        options.useGenericCalcs = false;
    }

    const discoveryOutranksGenericCalcs = dropOverridesForDiscovery && Boolean(state.featureToggles?.F298789_ASP_PG_redesign);

    if (discoveryOutranksGenericCalcs) {
        // Left empty, like the discovery branch below: the search is answered by the term and down payment in the
        // request override node, so this call builds no overrides at all.
    } else if (options.useGenericCalcs) {
        // Without the request's own term list this builder falls back to the deal's selected term, attaching the rate
        // to the wrong term for a forced call.
        overrides = createPaymentOverridesForGenericCalcs(state, offerType, {
            ...options,
            ...(options.forceUseGenericCalcs && isTermList ? { termList } : undefined)
        });
    } else if (dropOverridesForDiscovery) {
        // Left empty: the program search is answered by the term and down payment, which travel in the request override node.
    } else if (isTermList && !excludeOverrides && shouldApplyOverrides) {
        overrides = createPaymentOverrides(state, termList, offerType, options);
    } else if (excludeOverrides) {
        overrides = buildVehicleSwitchManualIncentivesOverride(state, action, options);
    }

    if (shouldApplyProgramMaxResidualizedMsrp(action, offerType)) {
        const { term, maxResidualizedMsrp, maxResidualizedMsrpApplied } = action.payload.programData;
        const byTerm = { ...((overrides as GenericObject).byTerm ?? {}) };
        byTerm[term] = {
            ...byTerm[term],
            maxResidualizedMsrp,
            maxResidualizedMsrpApplied
        };
        (overrides as GenericObject).byTerm = byTerm;
    }

    // DE720579 (gated by overrideTaxFixes_US1984109): make SV mimic MV by sending the saved
    // manualTotalTax so Payment Services doesn't recompute default taxes over the override.
    const enableOverrideTaxFixes = state.featureToggles?.overrideTaxFixes_US1984109;
    const includeManualTotalTax = enableOverrideTaxFixes || !options.enableOverridesForSalesView;

    const nonCashOverrides = {
        ...overrides,
        ...(manualTotalTax != null && includeManualTotalTax ? { manualTotalTax } : undefined),
        ...(taxOverrideUpfront != null && offerType === LEASE && { taxOverrideUpfront }),
        ...(capitalizeFirstPayment != null && offerType === LEASE && { capitalizeFirstPayment })
    };

    return nonCashOverrides;
};
const getManualTaxItems = (
    state: GenericObject,
    offerType: OfferType,
    options: RequestOverrideOptions
): AcceleronTaxItems | undefined => {
    // Scope to the request's offer type, not the deal's current one (see getNonCashOverrides).
    const manualTaxItems = offerSelectors.getManualTaxItemsForPayment(state, options.enableOverridesForSalesView, offerType);

    if (manualTaxItems.length > 0 && !offerSelectors.hasManualTotalTax(state, offerType)) {
        return manualTaxItems;
    }

    return undefined;
};

const isManagerViewAppliedProgram = (action: PaymentReduxAction): boolean =>
    action.type === APPLIED_PROGRAM && action.meta?.sourceApplication === ApplicationCodes.MV;

const shouldApplyProgramMaxResidualizedMsrp = (action: PaymentReduxAction, offerType: OfferType): boolean =>
    offerType === LEASE && isManagerViewAppliedProgram(action) && action.payload?.programData?.maxResidualizedMsrp != null;

const shouldUpdateAcqFeeForAppliedProgram = (action: PaymentReduxAction, feesOverrides: FeeOverride[] | undefined): boolean => {
    return (
        isManagerViewAppliedProgram(action) &&
        Boolean(feesOverrides) &&
        Array.isArray(feesOverrides) &&
        action.payload?.programData?.acqFee != null
    );
};

const setAcqFeeOverrideForAppliedProgram = (feesOverrides: FeeOverride[], acqFee: number): FeeOverride[] => {
    return feesOverrides.map((fee) => {
        if (fee && typeof fee === 'object' && fee.feeTypeId === ACQUISITION_FEE_ID) {
            return {
                ...fee,
                feeAmount: acqFee
            };
        }
        return fee;
    });
};
export const getFeesOverride = ({
    state,
    offerType,
    action,
    nonSelectedTermHonoringSV = false
}: FeesOverrideParams): FeeOverride[] | undefined => {
    const storedFeesOverrides = nonSelectedTermHonoringSV
        ? offerSelectors.getGovernmentFeesOverride(state, offerType)
        : offerSelectors.getFeesOverride(state, offerType);

    const feesOverrides = shouldUpdateAcqFeeForAppliedProgram(action, storedFeesOverrides)
        ? setAcqFeeOverrideForAppliedProgram(storedFeesOverrides!, action.payload.programData.acqFee)
        : storedFeesOverrides;

    const frozenLenderFees = nonSelectedTermHonoringSV
        ? undefined
        : (getActionMetaValue(action, FROZEN_LENDER_FEE_OVERRIDES_META) as FeeOverride[] | undefined);
    if (frozenLenderFees?.length) {
        const overriddenFeeTypeIds = new Set((feesOverrides ?? []).map((fee: FeeOverride) => fee.feeTypeId));
        return [...(feesOverrides ?? []), ...frozenLenderFees.filter((fee) => !overriddenFeeTypeIds.has(fee.feeTypeId))];
    }

    return feesOverrides;
};

export const getProgramFeesOverride = ({
    state,
    offerType,
    action,
    options = {}
}: ProgramFeesOverrideParams): FeeOverride[] | undefined => {
    let feesOverride = getFeesOverride({
        state,
        offerType,
        action,
        nonSelectedTermHonoringSV: options.nonSelectedTermHonoringSV
    });

    if (options.isProgramCall && offerType === LEASE) {
        feesOverride = [];
    }
    return feesOverride;
};

export const getOverridesByTerm = (termMonths: number | undefined, overrides: GenericObject = {}): GenericObject | undefined => {
    if (!termMonths || !overrides?.byTerm?.[termMonths]?.program) {
        return overrides?.byTerm;
    }
    const { program: _program, ...rest } = overrides.byTerm[termMonths];
    return { ...overrides.byTerm, [termMonths]: rest };
};

/**
 * The last-known program data for one term, in the shape the payment orchestrator merges into
 * `overrides.byTerm` when it retries a failed program search as a user program.
 */
type UpqTermSnapshot = {
    rateType?: string;
    sellRate?: number;
    buyRate?: number;
    baseResidualType?: ResidualValueType;
    baseResidualValue?: number;
    programResidualValue?: number;
    adjustedResidualType?: ResidualValueType;
    adjustedResidualValue?: number;
    mileageCharge?: number;
};

/**
 * The residual figures this term was last priced at, as a complete unit-and-value set.
 *
 * Reuses the same resolution the user-program-quotes override builder uses, for the same reason:
 * Payment Services rejects a residual value carrying no unit and a unit carrying no value, so the
 * pair travels together or the term is left incomplete and the orchestrator declines to retry it.
 */
const resolveSnapshotResiduals = (state: GenericObject, offerType: OfferType, months: number): Partial<UpqTermSnapshot> => {
    const source = pickBaseResidualSource([getLenderResidualForTerm(state, offerType, months)]);
    const unit = resolveCompleteResidualUnit(source);
    if (unit == null) {
        return {};
    }

    const isDollar = unit === RESIDUAL_DOLLAR;
    // The adjusted residual comes off this term, not off the deal's selected term. Two reasons: the
    // deal-level selectors resolve against the deal's *current* offer type, which is the wrong offer
    // when the Alt Deal Structure slideout prices a lease while the deal sits on finance; and
    // `getAdjustedResidualValue` coerces an absent value to 0, which the shared required-fields rule
    // reads as present. The term carries both units and leaves them undefined when there is nothing.
    const term = offerSelectors.getTermByOfferTypeAndMonths(state, offerType, months);
    const adjusted = isDollar ? term?.adjustedResidualValue : term?.adjustedResidualPercent;

    return {
        baseResidualType: unit,
        baseResidualValue: isDollar ? source?.baseResidualAmount : source?.baseResidualPercent,
        programResidualValue: isDollar ? source?.programResidual : source?.programResidualPercent,
        ...(adjusted != null ? { adjustedResidualType: unit, adjustedResidualValue: adjusted } : undefined)
    };
};

/**
 * The sellRate/rateType/buyRate set a UPQ snapshot term should carry when falling back to a priced
 * rate, or nothing (leaving `rateByTerm[term]` as-is) when there's an override or no fallback exists.
 *
 * An override wins as-is: `rateByTerm[term]` is already a paired, plausibility-filtered set from
 * `getPaymentGridOverridesByTerm`. Otherwise this falls back to what the term is actually priced at —
 * same precedence `restoreHeldValues` uses for the client-side program fallback — but every field
 * must come off that SAME term record (DE762937/DE747870). `getPaymentGridOverridesByTerm` can emit
 * a `buyRate` with no `sellRate` at all (a buy-rate-only override is a tolerated shape there), paired
 * against a `rateType` this fallback is about to replace — so a successful fallback must also replace
 * `buyRate` with the priced term's own, even when that means clearing a stale one, or the two would
 * describe different sources under one label.
 *
 * `resolveTermRateType` supplies the term's own defaulted-and-validated unit (APR/MoneyFactor per
 * offer type when the term carries no explicit `rateType`), checked against both the term's own
 * sellRate and buyRate together, `undefined` when either is implausible for it — never the
 * override's label. A term missing its own label is not evidence that the override's label
 * describes it.
 */
const resolveUpqSnapshotRate = (
    state: GenericObject,
    offerType: OfferType,
    term: number,
    rateOverride?: { rateType?: string; sellRate?: number; buyRate?: number }
): Partial<UpqTermSnapshot> => {
    if (rateOverride?.sellRate != null) {
        return { sellRate: rateOverride.sellRate, rateType: rateOverride.rateType, buyRate: rateOverride.buyRate };
    }

    const pricedTerm = offerSelectors.getTermByOfferTypeAndMonths(state, offerType, term);
    if (pricedTerm?.sellRate == null || offerType === CASH) {
        return {};
    }

    const rateType = resolveTermRateType(pricedTerm, offerType);
    return rateType != null ? { sellRate: pricedTerm.sellRate, rateType, buyRate: pricedTerm.buyRate } : {};
};

/**
 * Snapshot each requested term's last-known program data so a failed program search can be re-priced
 * as a user program rather than returning no payment.
 *
 * This is not an override and carries no user intent — it rides beside `overrides` on the offer node
 * and loses to anything the user actually set. It must stay outside `overrides`, because the
 * program-quotes request mapper branches on whether that node is empty; folding the snapshot in would
 * change what the first attempt sends.
 *
 * Completeness is deliberately not judged here. The orchestrator merges the snapshot under the deal's
 * real overrides and only then knows whether a term can be priced, so it owns that check — filtering
 * twice against the same rule is how the two answers drift apart.
 */
export const createUpqSnapshotByTerm = (
    state: GenericObject,
    offerType: OfferType,
    termList: number[] = [],
    options: RequestOverrideOptions = {}
): Record<number, UpqTermSnapshot> | undefined => {
    const terms = termList.filter((term): term is number => term != null);
    if (!terms.length) {
        return undefined;
    }

    const rateByTerm = getPaymentGridOverridesByTerm(state, offerType, { ...options, termList: terms }) as Record<
        number,
        { rateType?: string; sellRate?: number; buyRate?: number }
    >;
    const mileageCharge = offerSelectors.getMileageChargeOverrideByOfferType(state, offerType);

    // getPaymentGridOverridesByTerm reduces over exactly the terms handed to it, so every one has an
    // entry, and the empty-termList case already returned above — neither needs a fallback.
    return terms.reduce((acc: Record<number, UpqTermSnapshot>, term) => {
        acc[term] = {
            ...rateByTerm[term],
            ...resolveUpqSnapshotRate(state, offerType, term, rateByTerm[term]),
            ...(offerType === LEASE ? resolveSnapshotResiduals(state, offerType, term) : undefined),
            ...(offerType === LEASE && mileageCharge != null ? { mileageCharge } : undefined)
        };
        return acc;
    }, {});
};

const hasEntries = (list: unknown): boolean => Array.isArray(list) && list.length > 0;

export const requestSelectsProgramIncentives = (offerTypeNode: GenericObject | undefined): boolean => {
    const incentives = offerTypeNode?.incentives;
    const servicePicksStandards = !Array.isArray(incentives?.nonConditional) && offerTypeNode?.excludeStdIncs !== true;
    const byTerm: Record<string, GenericObject> = offerTypeNode?.overrides?.byTerm ?? {};
    return (
        servicePicksStandards ||
        hasEntries(incentives?.conditional) ||
        hasEntries(incentives?.nonConditional) ||
        hasEntries(offerTypeNode?.overrides?.manualIncentives?.incentiveIds) ||
        Object.values(byTerm).some((termOverride) => termOverride?.incentives != null)
    );
};

/**
 * Attach the term snapshot beside `overrides`, never inside it.
 *
 * Cash is excluded because it has no rate, term or lender program and never travels as a user
 * program. A roll call is excluded because it is not a program search. Balloon is excluded for now:
 * F283308 covers finance and lease only, and balloon's user-program shape is unverified — it emits no
 * production telemetry, so there is nothing to check a guess against.
 *
 * ShopperPlatform is excluded by *where this is called*, not by anything checked here: it sets neither
 * `isManagerView` nor `enableOverridesForSalesView`, so `setRequestOverrides` returns before reaching
 * this. `useUpqRetry` does not carry that scoping — it is only the toggle and the endpoint. Move this
 * call above that early return and SP starts sending the carrier.
 */
const setUpqSnapshot = (
    state: GenericObject,
    requestPaymentNode: PaymentOverride,
    offerType: OfferType,
    options: RequestOverrideOptions
): void => {
    if (!options.useUpqRetry || options.isRollToCall || (offerType !== FINANCE && offerType !== LEASE)) {
        return;
    }

    if (
        isGridIncentiveManagementEnabled(state, offerType) &&
        !options.isFlexBuyGridCall &&
        requestSelectsProgramIncentives(requestPaymentNode[offerType] as GenericObject)
    ) {
        return;
    }

    const upqByTerm = createUpqSnapshotByTerm(state, offerType, requestPaymentNode[offerType]!.termList, options);
    if (upqByTerm) {
        (requestPaymentNode[offerType] as GenericObject).upqByTerm = upqByTerm;
    }
};

type TermListRequestNode = Pick<IPaymentOverrideFinance, 'termList' | 'selectedTerm' | 'incentives'>;

type ByTermOverrides = { byTerm?: Record<number, GenericObject & { incentives?: IIncentives }> };

const readSelectedProgramIdentity = (state: GenericObject, offerType: OfferType): string | null =>
    offerSelectors.getTermProgramIdentity(offerSelectors.getSelectedTermByOfferType(state, offerType));

const findTermEligibility = (
    state: GenericObject,
    offerType: OfferType,
    months: number,
    selectedProgramIdentity: string | null
): offerSelectors.TermEligibility | null => {
    const offer = offerSelectors.getOffer(state);
    return offerSelectors.findTermEligibilityForMonths(
        offerSelectors.getAllTermsFromOffer(offer),
        offerSelectors.getTermsIdByOfferTerms(offer, offerType),
        months,
        selectedProgramIdentity
    );
};

/**
 * The backend replaces the whole per-term node, so `nonConditional` is carried as is and left off
 * when the offer level never set it.
 */
const buildNarrowedIncentivesNode = (conditional: number[], nonConditional: number[] | undefined): IIncentives =>
    nonConditional === undefined ? { conditional } : { conditional, nonConditional };

const hasOwnOverride = (overrides: ByTermOverrides, months: number): boolean =>
    Object.keys(overrides.byTerm?.[months] ?? {}).length > 0;

const writeTermIncentives = (overrides: ByTermOverrides, months: number, incentives: IIncentives): void => {
    overrides.byTerm = {
        ...overrides.byTerm,
        [months]: { ...overrides.byTerm?.[months], incentives }
    };
};

/**
 * A term with no usable data is left to inherit the deal's node. Dropping `nonConditional` instead would price it
 * with no standards in Manager View, where the offer already says `excludeStdIncs: true` whenever it lists them.
 */
const buildIncentivesForTerm = (
    offerConditional: number[],
    offerNonConditional: number[] | undefined,
    compatibleIds: Set<number> | null
): IIncentives | null => {
    if (compatibleIds == null) return null;

    const narrowed = offerConditional.filter((id) => compatibleIds.has(id));
    return narrowed.length === offerConditional.length ? null : buildNarrowedIncentivesNode(narrowed, offerNonConditional);
};

/**
 * Builds each other term's own incentives from what Payment Services recorded as compatible for it, for
 * program-quotes calls. The selected term is never touched, and a term with no per-term override is never
 * given one: a new entry changed the program Payment Services priced that term under. Assumes compatible ids do not depend on the
 * ids requested, which was checked on one program only.
 */
const applyPerTermConditionalIncentiveFiltering = (
    state: GenericObject,
    requestPaymentNode: PaymentOverride,
    offerType: OfferType,
    overrides: ByTermOverrides
): void => {
    if (offerType === CASH) return;

    const offerNode = requestPaymentNode[offerType] as TermListRequestNode | undefined;
    const termList = offerNode?.termList;
    const currentTerm = offerNode?.selectedTerm;
    const offerConditional = offerNode?.incentives?.conditional;
    const offerNonConditional = offerNode?.incentives?.nonConditional;

    if (!termList || termList.length <= 1 || !Array.isArray(offerConditional) || offerConditional.length === 0) return;

    const selectedProgramIdentity = readSelectedProgramIdentity(state, offerType);

    for (const months of termList) {
        if (months === currentTerm || !hasOwnOverride(overrides, months)) continue;

        const compatibleIds = findTermEligibility(state, offerType, months, selectedProgramIdentity)?.compatibleIds ?? null;
        const incentives = buildIncentivesForTerm(offerConditional, offerNonConditional, compatibleIds);
        if (incentives) writeTermIncentives(overrides, months, incentives);
    }
};

const usesAppliedStandards = (candidates: IncentiveCandidates, flags: IncentiveFlags): boolean =>
    flags.isManagerView && !flags.includeStandardIncentives && !candidates.hasRecordedSelection;

/**
 * Null when the term's recorded standards are needed and Payment Services recorded none for it: no data writes
 * nothing, and only a recorded empty list may become `nonConditional: []`.
 */
const readTermAppliedStandardIds = (
    candidates: IncentiveCandidates,
    flags: IncentiveFlags,
    eligibility: offerSelectors.TermEligibility
): number[] | null => {
    if (!usesAppliedStandards(candidates, flags)) return [];
    if (!Array.isArray(eligibility.appliedIncentiveIds)) return null;
    return toStandardIds(
        eligibility.appliedIncentiveIds,
        (id) => candidates.standardIdSet.has(id) && eligibility.compatibleIds.has(id)
    );
};

const buildTermIncentives = (
    candidates: IncentiveCandidates,
    flags: IncentiveFlags,
    eligibility: offerSelectors.TermEligibility | null
): IIncentives | null => {
    if (eligibility == null) return null;

    const appliedStandardIds = readTermAppliedStandardIds(candidates, flags, eligibility);
    if (appliedStandardIds == null) return null;

    return buildIncentivesFromCandidates(candidates, flags, (id) => eligibility.compatibleIds.has(id), appliedStandardIds);
};

const sortedIds = (ids: number[] | undefined): number[] | undefined =>
    ids ? [...ids].sort((first, second) => first - second) : ids;

const hasSameIds = (left: number[] | undefined, right: number[] | undefined): boolean => {
    const sortedLeft = sortedIds(left);
    const sortedRight = sortedIds(right);
    return (
        sortedLeft === sortedRight ||
        (!!sortedLeft &&
            !!sortedRight &&
            sortedLeft.length === sortedRight.length &&
            sortedLeft.every((id, index) => id === sortedRight[index]))
    );
};

const isSameIncentives = (left: IIncentives, right: IIncentives): boolean =>
    hasSameIds(left.conditional, right.conditional) && hasSameIds(left.nonConditional, right.nonConditional);

/**
 * A term the builder cannot write for keeps the older conditional narrowing when that toggle is on too, so turning
 * the builder on never removes handling a term had before.
 */
const narrowSkippedTermIncentives = (
    offerNode: TermListRequestNode,
    eligibility: offerSelectors.TermEligibility | null,
    options: RequestOverrideOptions
): IIncentives | null => {
    const offerConditional = offerNode.incentives?.conditional;
    if (!options.useIncentivesOverrides || !Array.isArray(offerConditional) || offerConditional.length === 0) return null;

    return buildIncentivesForTerm(offerConditional, offerNode.incentives?.nonConditional, eligibility?.compatibleIds ?? null);
};

const isPerTermIncentiveBuildPossible = (state: GenericObject, options: RequestOverrideOptions): boolean =>
    isProgramIncentivesRestorationEnabled(state) && !(options.isManagerView && options.isProgramCall);

/**
 * Per-term incentives from each term's own recorded eligibility, written only where they differ from the offer
 * level: any per-term key makes the UPQ retry decline, and the sibling rescue skips such offers. The selected
 * term, a term with no recorded data, and a term with no per-term override of its own are never written: an entry
 * holding only incentives once made Payment Services price a term under a different program. Assumes compatible ids
 * do not depend on the ids requested.
 */
const applyPerTermIncentiveOverrides = (
    state: GenericObject,
    requestPaymentNode: PaymentOverride,
    offerType: OfferType,
    overrides: ByTermOverrides,
    options: RequestOverrideOptions
): void => {
    if (offerType === CASH) return;

    const offerNode = requestPaymentNode[offerType] as TermListRequestNode | undefined;
    const termList = offerNode?.termList;
    if (!offerNode?.incentives || !termList || termList.length <= 1) return;

    const flags: IncentiveFlags = {
        isProgramCall: !!options.isProgramCall,
        isManagerView: !!options.isManagerView,
        includeStandardIncentives: !!options.includeStandardIncentives,
        honorsDealerStandardExclusion: offerSelectors.shouldHonorDealerStandardExclusion(state, offerType)
    };
    const candidates = readIncentiveCandidates(
        state,
        offerType,
        offerSelectors.getMappedIdsForSelectedIncentivesByOfferType(state, offerType) || [],
        flags.isProgramCall,
        flags.isManagerView,
        !!options.isUpdateSelectedIncentivesCall
    );
    const selectedProgramIdentity = readSelectedProgramIdentity(state, offerType);

    for (const months of termList) {
        if (months === offerNode.selectedTerm || !hasOwnOverride(overrides, months)) continue;

        const eligibility = findTermEligibility(state, offerType, months, selectedProgramIdentity);
        const incentives =
            buildTermIncentives(candidates, flags, eligibility) ?? narrowSkippedTermIncentives(offerNode, eligibility, options);
        if (incentives && !isSameIncentives(incentives, offerNode.incentives)) writeTermIncentives(overrides, months, incentives);
    }
};

export const setRequestOverrides = (
    state: GenericObject,
    requestPaymentNode: PaymentOverride,
    offerType: OfferType,
    action: PaymentReduxAction,
    options: RequestOverrideOptions = {}
): boolean => {
    const shouldSkipOverrides =
        !options.isManagerView && !options.enableOverridesForSalesView && !options.enableOverridesForCreditDecision;

    // OPL requires capitalizeFirstPayment: false even when other overrides are skipped (e.g., ShopperPlatform)
    if (shouldSkipOverrides) {
        const paymentFrequency = getPayloadPaymentFrequency(state, offerType);
        const isOnePayLease = paymentFrequency === PaymentScheduleTypes.OnePayLease;

        if (isOnePayLease && offerType === LEASE) {
            (requestPaymentNode[offerType]!.overrides as GenericObject) = {
                ...requestPaymentNode[offerType]!.overrides,
                capitalizeFirstPayment: false
            };
            return true;
        }
        return false;
    }

    const overrides =
        offerType === CASH
            ? getCashOverrides(state, options, action)
            : getNonCashOverrides(state, action, offerType, requestPaymentNode[offerType]!.termList, options);

    const manualTaxItems = getManualTaxItems(state, offerType, options);
    if (manualTaxItems) {
        overrides.manualTaxItems = manualTaxItems;
    }
    const termMonths = options.isProgramCall ? getSelectedTermMonths(state, offerType) : undefined;

    overrides.feesOverride = getProgramFeesOverride({ state, offerType, action, options });

    const byTerm = getOverridesByTerm(termMonths, overrides);

    if (byTerm) {
        (overrides as GenericObject).byTerm = byTerm;
    }

    if (options.usePerTermIncentiveOverrides && isPerTermIncentiveBuildPossible(state, options)) {
        applyPerTermIncentiveOverrides(state, requestPaymentNode, offerType, overrides as ByTermOverrides, options);
    } else if (options.useIncentivesOverrides) {
        applyPerTermConditionalIncentiveFiltering(state, requestPaymentNode, offerType, overrides as ByTermOverrides);
    }

    if (overrides && Object.keys(overrides)?.length) {
        requestPaymentNode[offerType]!.overrides = overrides;
    }

    setUpqSnapshot(state, requestPaymentNode, offerType, options);

    return Object.keys(requestPaymentNode).length > 0;
};

const getTradeInsOverrides = (state: GenericObject, isRollToCall: boolean) => {
    const { tradeIns } = offerSelectors.getOfferDetails(state);
    if (state.featureToggles?.DE784135_Enable_Trade_Edit_After_Roll_To) {
        return isRollToCall ? tradeIns : undefined;
    }
    return tradeIns;
};

export const mapTradeInDetails = (
    state: GenericObject,
    requestPaymentNode: PaymentWithDealerIdRequest,
    hostSelectors: HostSelectors,
    isRollToCall = false
): void => {
    const isNegativeTradeInFixEnabled = Boolean(state.featureToggles?.DE770955_Enable_Negative_Trade_In_Fix);
    const tradeIns: ITradeIn[] =
        (hostSelectors?.getIncludedTradeInItems
            ? hostSelectors.getIncludedTradeInItems(state)
            : state.tradeIns?.items
                  ?.filter((tradeInItem: any) => !tradeInItem.isDeleted && tradeInItem.tradeIn?.shouldIncludeTradeIn)
                  ?.map?.(({ tradeIn }: { tradeIn: any }) => tradeIn)) ?? [];
    if (hostSelectors?.shouldUseMultiTradeIns?.(state, tradeIns)) {
        const tradeInsOverrides = getTradeInsOverrides(state, isRollToCall);

        const mappedTradeIns = tradeIns.map((tradeIn) => {
            const tradeInOverride = tradeInsOverrides?.find(
                (override) => override?.callerItemId != null && override.callerItemId === tradeIn.callerItemId
            );

            const mergedTradeIn = tradeInOverride ? { ...tradeIn, ...tradeInOverride } : tradeIn;

            const shouldIncludeTradeIn = mergedTradeIn?.shouldIncludeTradeIn ?? true;
            return {
                ...setTradeIn(shouldIncludeTradeIn, mergedTradeIn, tradeIn, isNegativeTradeInFixEnabled),
                callerItemId: tradeInOverride?.callerItemId ?? tradeIn.callerItemId
            };
        });

        const filteredTradeIns = mappedTradeIns.filter((tradeIn) => tradeIn.value > 0 || tradeIn.owed > 0);

        requestPaymentNode.tradeIns = filteredTradeIns.length > 0 ? filteredTradeIns : undefined;
    } else {
        const tradeInValues = hostSelectors?.getTradeIn ? hostSelectors.getTradeIn(state) : state.tradeIn;
        const { tradeIn: tradeInOverrides } = offerSelectors.getOfferDetails(state);
        const tradeIn = { ...tradeInValues, ...tradeInOverrides };
        const shouldIncludeTradeIn = tradeIn?.shouldIncludeTradeIn ?? true;

        requestPaymentNode.tradeIn = setTradeIn(shouldIncludeTradeIn, tradeIn, tradeInValues, isNegativeTradeInFixEnabled);
    }
};

/**
 * `isNegativeTradeInFixEnabled` is DE770955's toggle. Off reproduces the pre-fix clamp - a $0-allowance,
 * real-payoff trade-in (negative equity) reads `owed` as 0 and drops out of the request entirely. On,
 * `owed` always reflects the real payoff, matching the client-side Net Trade selector this was never
 * supposed to disagree with.
 */
export const setTradeIn = (
    shouldIncludeTradeIn: boolean,
    tradeIn: ITradeIn,
    tradeInValues: any,
    isNegativeTradeInFixEnabled = false
): IPaymentTradeIn => {
    if (!shouldIncludeTradeIn) {
        return {
            financeNegativeEquity: false,
            owed: 0,
            value: 0
        };
    }

    const acv = tradeIn?.acv;
    const value = tradeIn?.value || 0;
    const type = tradeIn?.ownershipType?.toLowerCase() === LEASE;
    const owed = value === 0 && !isNegativeTradeInFixEnabled ? 0 : tradeIn?.amountOwed || 0;
    const financeNegativeEquity = value < owed && shouldIncludeTradeIn;
    const isBuyerRegisteredOwner = tradeIn?.isBuyerRegisteredOwner;
    const isSellingVehicleRegisteredInSameState = tradeIn?.isSellingVehicleRegisteredInSameState;

    let tradeInType;

    if (tradeInValues) {
        if (type) {
            tradeInType = 'Leased';
        } else if (owed) {
            tradeInType = 'Finance';
        } else {
            tradeInType = 'Owned';
        }
    }

    return {
        financeNegativeEquity,
        owed,
        value,
        tradeInType,
        ...(acv && { acv }),
        ...(isBuyerRegisteredOwner != null && { isBuyerRegisteredOwner }),
        ...(isSellingVehicleRegisteredInSameState != null && { isSellingVehicleRegisteredInSameState })
    };
};

export const setRequestMenuItems = (
    state: GenericObject,
    requestPaymentNode: IPaymentOverrideLease | IPaymentOverrideFinance | IPaymentOverrideCash,
    hostSelectors: HostSelectors
): void => {
    const menuItems = hostSelectors.getPaymentProducts?.(state);

    if (menuItems) {
        requestPaymentNode.menuItems = menuItems;
    }
};

export const setRequestMenuSelectionsId = (
    state: GenericObject,
    requestPaymentNode: IPaymentOverrideLease | IPaymentOverrideFinance | IPaymentOverrideCash,
    hostSelectors: HostSelectors
): void => {
    const menuSelectionsIdToUse = hostSelectors?.getMenuSelectionsId
        ? hostSelectors.getMenuSelectionsId(state)
        : state.vehicleProtection?.menuSelectionsId || '';

    if (menuSelectionsIdToUse) {
        requestPaymentNode.menuSelectionsId = menuSelectionsIdToUse;
    }
};

export const setBypassDraasSettingsCache = (state: any, requestPaymentNode: PaymentOverride): void => {
    if (state.widgetSettings?.bypassDraasSettingsCache) {
        requestPaymentNode.bypassDraasSettingsCache = true;
    }
};

export const setLocationId = (state: any, requestPaymentNode: PaymentOverride): void => {
    const locationId = getPaymentLocationId(state);
    if (locationId) {
        requestPaymentNode.locationId = locationId;
    }
};

export const setShopperBirthDateAndLastName = (
    requestPaymentNode: PaymentWithDealerIdRequest,
    birthDate: string,
    lastName: string
) => {
    if (birthDate) {
        requestPaymentNode.shopper = {
            ...requestPaymentNode.shopper,
            birthDate
        };
    }
    if (lastName) {
        requestPaymentNode.shopper = {
            ...requestPaymentNode.shopper,
            lastName
        };
    }
};

export const setShopper = (state: GenericObject, requestPaymentNode: PaymentWithDealerIdRequest): void => {
    const shopper = offerSelectors.getShopper(state);
    setShopperBirthDateAndLastName(requestPaymentNode, shopper.birthDate as string, shopper.lastName as string);

    if (!shopper.zip) return;
    if (shopper.streetAddress && shopper.city && shopper.state) {
        requestPaymentNode.shopper = {
            ...requestPaymentNode.shopper,
            zip: shopper.zip,
            city: shopper.city,
            state: shopper.state,
            birthDate: shopper.birthDate,
            streetAddress: shopper.streetAddress
        };
        return;
    }

    requestPaymentNode.shopper = {
        ...requestPaymentNode.shopper,
        zip: shopper.zip
    };
};

export const setFlexBuyPaymentGrid = (params: {
    state: GenericObject;
    paymentOverride: IPaymentOverrideFinance;
    initialPaymentDiscount: number[];
    requestPaymentNode: IPaymentOverride;
}): void => {
    const { state, paymentOverride, initialPaymentDiscount, requestPaymentNode } = params;
    const isFlexBuyPayment = offerSelectors.isFlexBuyPayment(state);
    const isFlexBuyPaymentGridEnabled = offerSelectors.getFlexBuyPaymentGridToggle(state);

    if (!isFlexBuyPayment || !isFlexBuyPaymentGridEnabled) return;

    const flexBuyPercents = paymentOverride.flexBuyPercents || [];
    const cashGoals = paymentOverride.flexBuyDownPayments || [];
    const discounts = flexBuyPercents.length ? flexBuyPercents : initialPaymentDiscount;

    requestPaymentNode.finance = {
        ...requestPaymentNode.finance,
        specialPaymentFormula: SpecialPaymentFormula.FordFlexBuy,
        quotesExpansionList: {
            ...requestPaymentNode.finance?.quotesExpansionList,
            ...(cashGoals?.length && { DOWNPAYMENT: cashGoals }),
            ...(discounts?.length && { INITIALPAYMENTDISCOUNT: discounts })
        }
    };
};

/**
 * Add profitability data to payment payload if available in state
 */
export const addProfitabilityDataToPayload = (
    state: GenericObject,
    paymentRequest: PaymentWithDealerIdRequest,
    offerType: OfferType
): void => {
    const profitabilityData: paymentServicesTypes.ProgramQuoteOutput = getProfitabilityForPayloadByDealType(state, offerType);

    if (!profitabilityData || !hasProfitabilityData(profitabilityData)) {
        return;
    }

    if (paymentRequest.payment[offerType]) {
        (paymentRequest.payment[offerType] as any) = {
            ...paymentRequest.payment[offerType],
            ...(profitabilityData.pricing && { pricing: profitabilityData.pricing }),
            ...(profitabilityData.profitDetails && { profit: profitabilityData.profitDetails }),
            ...(profitabilityData.proceeds && { proceeds: profitabilityData.proceeds })
        };
    }
};
